// @ts-nocheck
import {
  pgTable,
  serial,
  text,
  timestamp,
  integer,
  boolean,
  real,
  date,
  jsonb,
  numeric,
  decimal,
  index,
  uniqueIndex,
  check,
  bigserial,
} from 'drizzle-orm/pg-core';
import { relations, sql } from 'drizzle-orm';
import { createInsertSchema } from 'drizzle-zod';
import { z } from 'zod';

// Users table
export const users = pgTable('users', {
  id: serial('id').primaryKey(),
  username: text('username').unique().notNull(),
  password_hash: text('password_hash').notNull(),
  reset_token: text('reset_token'),
  reset_token_expiry: timestamp('reset_token_expiry'),
  full_name: text('full_name'),
  email: text('email'),
  role: text('role').default('user'),
  firstName: text('first_name'),
  lastName: text('last_name'),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
  is_active: boolean('is_active').default(true),
  emailVerified: boolean('email_verified').default(false),
  emailVerificationToken: text('email_verification_token'),
  emailVerificationExpiry: timestamp('email_verification_expiry'),
  login_attempts: integer('login_attempts').default(0),
  account_locked_until: timestamp('account_locked_until'),
  last_login_at: timestamp('last_login_at'),
  last_login_ip: text('last_login_ip'),
  password_changed_at: timestamp('password_changed_at'),
  two_factor_enabled: boolean('two_factor_enabled').default(false),
  two_factor_secret: text('two_factor_secret'),
  password: text('password'), // Legacy field, keeping for compatibility
  // Mention and notification system fields
  lastSeen: timestamp('last_seen'),
  isOnline: boolean('is_online').default(false),
  notificationPreferences: jsonb('notification_preferences').$type<{
    email: boolean;
    inApp: boolean;
    mentions: boolean;
    tasks: boolean;
  }>().default({ email: true, inApp: true, mentions: true, tasks: true }),
  avatarUrl: text('avatar_url'),
  displayName: text('display_name'),
});

// Customers table
export const customers = pgTable('customers', {
  id: serial('id').primaryKey(),
  name: text('name').notNull(),
  email: text('email'),
  phone: text('phone'),
  address: text('address'),
  postcode: text('postcode'),
  what3wordsAddress: text('what3words_address'),
  source: text('source'),
  status: text('status'),
  electricitySupplier: text('electricity_supplier'),
  currentTariff: text('current_tariff'),
  electricityUsage: text('electricity_usage'),
  propertyType: text('property_type'),
  roofType: text('roof_type'),
  yearBuilt: text('year_built'),
  notes: text('notes'),
  additionalData: jsonb('additional_data'),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
  userId: integer('user_id'),
  pipelineStageId: integer('pipeline_stage_id'),
  pipelineNotes: text('pipeline_notes'),
  pipelineUpdatedAt: timestamp('pipeline_updated_at'),
  leadScore: integer('lead_score'),
  lastContactDate: timestamp('last_contact_date'),
  nextContactDate: timestamp('next_contact_date'),
  initialContactDate: timestamp('initial_contact_date'),
  siteAssessmentDate: timestamp('site_assessment_date'),
  dealValue: integer('deal_value'),
  externalProjectId: text('external_project_id'),
  opensolarProjectId: text('opensolar_project_id'),
  // Team assignment fields
  assignedTo: integer('assigned_to').references(() => users.id),
  assignmentDate: timestamp('assignment_date'),
  followUpDate: timestamp('follow_up_date'),
  assignmentNotes: text('assignment_notes'),
  assignmentPriority: text('assignment_priority'), // 'high', 'medium', 'low'
  // Lifecycle / archival fields
  archivedAt: timestamp('archived_at'),
  archivedReason: text('archived_reason'), // 'won', 'lost', 'not_qualified', 'manual', 'auto_stale'
  // U8-compatible tracking state. Defaults keep the previous binary's named
  // inserts valid while canonical producers remain disabled.
  pipelineVersion: integer('pipeline_version').notNull().default(0),
  websiteTrackingChoice: text('website_tracking_choice').$type<TrackingWebsiteChoice>(),
  websiteTrackingChoiceVersion: text('website_tracking_choice_version'),
  crmMetaLifecycleEligibility: text('crm_meta_lifecycle_eligibility'),
  crmPolicyVersion: text('crm_policy_version'),
  acquisitionSourceNormalized: text('acquisition_source_normalized').$type<TrackingAcquisitionSource>(),
  acquisitionSourceVersion: text('acquisition_source_version'),
  metaSubjectId: text('meta_subject_id'),
});

// Leads table
export const leads = pgTable('leads', {
  id: serial('id').primaryKey(),
  name: text('name').notNull(),
  email: text('email'),
  phone: text('phone'),
  address: text('address'),
  postcode: text('postcode'),
  source: text('source'),
  status: text('status'),
  notes: text('notes'),
  pipelineStageId: integer('pipeline_stage_id'),
  pipelineNotes: text('pipeline_notes'),
  pipelineUpdatedAt: timestamp('pipeline_updated_at'),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// Pipeline stages table
export const pipelineStages = pgTable('pipeline_stages', {
  id: serial('id').primaryKey(),
  name: text('name').notNull(),
  description: text('description'),
  order: integer('order').notNull(),
  color: text('color'),
  stageType: text('stage_type').default('open'), // 'open' | 'closed_won' | 'closed_lost'
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
  trackingKey: text('tracking_key'),
}, (table) => [
  uniqueIndex('pipeline_stages_tracking_key_uq')
    .on(table.trackingKey)
    .where(sql`${table.trackingKey} is not null`),
]);

// Proposals table
export const proposals = pgTable('proposals', {
  id: serial('id').primaryKey(),
  customerId: integer('customer_id').references(() => customers.id),
  title: text('title'),
  systemSize: text('system_size'),
  panelCount: integer('panel_count'),
  inverterType: text('inverter_type'),
  estimatedCost: real('estimated_cost'),
  estimatedSavings: real('estimated_savings'),
  paybackPeriod: text('payback_period'),
  description: text('description'),
  status: text('status').default('draft'),
  totalPrice: real('total_price'),
  totalCost: real('total_cost'),
  notes: text('notes'),
  metadata: jsonb('metadata'),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// Installations table - Complete with all DNO and checklist fields
export const installations = pgTable('installations', {
  id: serial('id').primaryKey(),
  customerId: integer('customer_id').references(() => customers.id),
  proposalId: integer('proposal_id').references(() => proposals.id),
  status: text('status').default('loa_mpan'),
  installationAddress: text('installation_address'),
  systemType: text('system_type'),
  designSystem: text('design_system'),
  openSolarReference: text('open_solar_reference'),
  contractSigned: timestamp('contract_signed'),
  scheduledDate: timestamp('scheduled_date'),
  completedDate: timestamp('completed_date'),
  installationCost: text('installation_cost'),
  depositPaid: timestamp('deposit_paid'),
  sevenDayPaymentDate: timestamp('seven_day_payment_date'),
  finalPaymentDate: timestamp('final_payment_date'),
  notes: text('notes'),
  installerNames: text('installer_names'),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
  depositAmount: text('deposit_amount'),
  sevenDayPaymentAmount: text('seven_day_payment_amount'),
  finalPaymentAmount: text('final_payment_amount'),

  // DNO Application fields
  dnoProvider: text('dno_provider'),
  dnoApplicationType: text('dno_application_type'),
  dnoApplicationSent: timestamp('dno_application_sent'),
  dnoSystemReference: text('dno_system_reference'),
  dnoCaseReference: text('dno_case_reference'),
  dnoApproved: timestamp('dno_approved'),
  fastTrackReference: text('fast_track_reference'),
  dnoAdditionalComments: text('dno_additional_comments'),
  dnoExportLimitation: text('dno_export_limitation'),
  dnoDeclaredNetCapacity: text('dno_declared_net_capacity'),
  dnoFees: jsonb('dno_fees'),
  dnoNotes: text('dno_notes'),

  // System details
  mpanNumber: text('mpan_number'),
  exportMpan: text('export_mpan'),
  inverterSerialNumber: text('inverter_serial_number'),
  commissioningDate: timestamp('commissioning_date'),
  additionalPaymentDate: timestamp('additional_payment_date'),
  additionalPaymentAmount: text('additional_payment_amount'),
  batterySerialNumber: text('battery_serial_number'),
  purchaseOrderNumber: text('purchase_order_number'),
  cost: text('cost'),

  // Checklist fields
  checklistExportMpanReceived: text('checklist_export_mpan_received'),
  checklistSwitchExportConnect: text('checklist_switch_export_connect'),
  checklistLaminateSchematic: text('checklist_laminate_schematic'),
  checklistLaminateStartupShutdown: text('checklist_laminate_startup_shutdown'),
  checklistF17ContractManagement: text('checklist_f17_contract_management'),
  checklistConsolidatedForm: text('checklist_consolidated_form'),
  checklistDnoCommissioningSent: text('checklist_dno_commissioning_sent'),
  checklistEicCompleted: text('checklist_eic_completed'),
  checklistStructuralSurvey: text('checklist_structural_survey'),
  checklistShadeReport: text('checklist_shade_report'),
  checklistSolarPvDesignCalculations: text('checklist_solar_pv_design_calculations'),
  checklistSendSunsynkApp: text('checklist_send_sunsynk_app'),
  checklistAmendmentsProposalInvoice: text('checklist_amendments_proposal_invoice'),
  checklistHiesReturnCompleted: text('checklist_hies_return_completed'),
  checklistPartPCertificateCreated: text('checklist_part_p_certificate_created'),
  checklistMcsCertification: text('checklist_mcs_certification'),
  checklistFinancePhoenixPortal: text('checklist_finance_phoenix_portal'),
  checklistInverterSerialToStock: text('checklist_inverter_serial_to_stock'),
  checklistFinalPaymentReceived: text('checklist_final_payment_received'),
  checklistMcsCertificateProvided: text('checklist_mcs_certificate_provided'),
  checklistEicCertificateProvided: text('checklist_eic_certificate_provided'),
  checklistPartPBuildingRegsCertificate: text('checklist_part_p_building_regs_certificate'),
  checklistOwnerManual: text('checklist_owner_manual'),
  checklistStructuralReport: text('checklist_structural_report'),
  checklistProposalUpdated: text('checklist_proposal_updated'),
  checklistSchematic: text('checklist_schematic'),
  checklistSolarPanelDetails: text('checklist_solar_panel_details'),
  checklistSolarPvFormsConsolidated: text('checklist_solar_pv_forms_consolidated'),
  checklistInverterWarranty: text('checklist_inverter_warranty'),
  checklistFinalInvoice: text('checklist_final_invoice'),
  checklistCompleteOpenSolar: text('checklist_complete_open_solar'),
  checklistCustomerSatisfactionSurvey: text('checklist_customer_satisfaction_survey'),
  checklistDiariseYearCheck: text('checklist_diarise_year_check'),
  checklistAddSerialNumbersStock: text('checklist_add_serial_numbers_stock'),
  checklistBookScaffolding: text('checklist_book_scaffolding'),
  checklistPoForScaffold: text('checklist_po_for_scaffold'),
  checklistLetterScaffoldInstallDate: text('checklist_letter_scaffold_install_date'),
  checklistPvPlannerContractors: text('checklist_pv_planner_contractors'),
  checklistScaffoldRemoved: text('checklist_scaffold_removed'),
  
  // New HIES and EVCP checklist fields for grid tracking
  checklistHiesRegistered: text('checklist_hies_registered'),
  checklistEvcpApplication: text('checklist_evcp_application'),
  
  // New date fields for better tracking
  hiesRegisteredDate: timestamp('hies_registered_date'),
  hiesCompletedDate: timestamp('hies_completed_date'),
  stripScaffDate: timestamp('strip_scaff_date'),

  // Document arrays
  financeDocuments: jsonb('finance_documents'),
  dnoDocuments: jsonb('dno_documents'),
  onsiteDocuments: jsonb('onsite_documents'),
  postInstallDocuments: jsonb('post_install_documents'),
  handoverDocuments: jsonb('handover_documents'),
  maintainingDocuments: jsonb('maintaining_documents'),
  technicalDocuments: jsonb('technical_documents'),
  contractDocuments: jsonb('contract_documents'),

  // Scaffold images
  scaffoldImageUrl1: text('scaffold_image_url_1'),
  scaffoldImageUrl2: text('scaffold_image_url_2'),
  scaffoldImageUrl3: text('scaffold_image_url_3'),
  scaffoldImageNotes: text('scaffold_image_notes'),

  // Additional system fields
  siteSurveyId: integer('site_survey_id'),
  systemSize: real('system_size'),
  panelCount: integer('panel_count'),
  inverterType: text('inverter_type'),
  batteryIncluded: boolean('battery_included'),
  totalCost: real('total_cost'),
  roofType: text('roof_type'),
  roofDirection: text('roof_direction'),
  shadingIssues: boolean('shading_issues'),
  electricalUpgradeRequired: boolean('electrical_upgrade_required'),
  scaffoldingRequired: boolean('scaffolding_required'),
  installationDate: timestamp('installation_date'),
  postcode: text('postcode'),
  installerName: text('installer_name'),
  installerContact: text('installer_contact'),
  equipmentList: jsonb('equipment_list'),
  warrantyInfo: text('warranty_info'),

  // === NEW FIELDS FROM SPREADSHEET ANALYSIS ===

  // System Specification
  panelModel: text('panel_model'), // Panel brand/model (e.g., "JA Solar 425W")
  batteryModel: text('battery_model'), // Battery model name
  batteryQty: integer('battery_qty'), // Number of batteries

  // Pre-Install Tracking
  folderCreated: boolean('folder_created'), // OneDrive folder created
  tradifyAdded: boolean('tradify_added'), // Added to Tradify
  jobBoardAdded: boolean('job_board_added'), // Added to job board
  jobBoardRemoved: boolean('job_board_removed'), // Removed from job board

  // DNO Additional
  dnoOfferLetterReceived: timestamp('dno_offer_letter_received'), // DNO offer letter date
  dnoDocusignStatus: text('dno_docusign_status'), // Docusign status
  dnoFeePaid: boolean('dno_fee_paid'), // DNO fee paid
  dnoLimitAddedToSurvey: boolean('dno_limit_added_to_survey'), // Export limit added to survey

  // Scheduling
  siteVisitDate: timestamp('site_visit_date'), // Site visit scheduled date
  sevenDayInvoiceIssued: boolean('seven_day_invoice_issued'), // 7-day invoice issued
  ramsCompleted: boolean('rams_completed'), // RAMS document completed
  schedulingRequired: boolean('scheduling_required').default(true), // Needs installation date scheduling

  // On-Site
  evcpResponse: text('evcp_response'), // EVCP response status
  onsiteSignoffCompleted: boolean('onsite_signoff_completed'), // On-site sign-off done
  systemAlterations: text('system_alterations'), // Notes on system changes
  balanceInvoiceIssued: boolean('balance_invoice_issued'), // Balance invoice issued
  nextStepsEmailSent: boolean('next_steps_email_sent'), // Next steps email sent
  evcpCommissioningSent: boolean('evcp_commissioning_sent'), // EVCP commissioning sent
  maintenanceScheduleProvided: boolean('maintenance_schedule_provided'), // Maintenance schedule given

  // Document tracking fields for Kanban view
  checklistInvoicePaid: text('checklist_invoice_paid'), // Invoice paid status
  checklistG99Commissioning: text('checklist_g99_commissioning'), // G99 commissioning status
  checklistSld: text('checklist_sld'), // Single Line Diagram status
  checklistDatasheets: text('checklist_datasheets'), // Datasheets status
  checklistInvoicesProvided: text('checklist_invoices_provided'), // Invoices provided status
});

// Document files table
export const documentFiles = pgTable('document_files', {
  id: serial('id').primaryKey(),
  filename: text('filename').notNull(),
  originalFilename: text('original_filename'), // Fixed: was originalName, should be originalFilename
  mimeType: text('mime_type'),
  fileSize: integer('file_size'),
  filePath: text('file_path'),
  category: text('category'),
  uploadedBy: integer('uploaded_by'),
  customerId: integer('customer_id'),
  installationId: integer('installation_id'),
  proposalId: integer('proposal_id'),
  metadata: jsonb('metadata'),
  oneDriveId: text('onedrive_id'), // OneDrive file ID for cloud storage
  createdAt: timestamp('created_at').defaultNow(),
});

// File contents table for document indexing
export const fileContents = pgTable('file_contents', {
  id: serial('id').primaryKey(),
  documentId: integer('document_id').references(() => documentFiles.id),
  content: text('content'),
  contentType: text('content_type'),
  extractedAt: timestamp('extracted_at').defaultNow(),
});

// Components table for system components
export const components = pgTable('components', {
  id: serial('id').primaryKey(),
  category: text('category').notNull(),
  name: text('name').notNull(),
  model: text('model'),
  unitCost: text('unit_cost'),
  createdAt: timestamp('created_at').defaultNow(),
});

// Proposal items table
export const proposalItems = pgTable('proposal_items', {
  id: serial('id').primaryKey(),
  proposalId: integer('proposal_id').references(() => proposals.id),
  componentId: integer('component_id').references(() => components.id),
  category: text('category').notNull(),
  name: text('name').notNull(),
  description: text('description'),
  model: text('model'),
  unitCost: text('unit_cost'),
  unitPrice: text('unit_price'),
  quantity: integer('quantity').default(1),
  subtotal: real('subtotal'),
  totalCost: real('total_cost'),
  createdAt: timestamp('created_at').defaultNow(),
});

// Installation milestones table
export const installationMilestones = pgTable('installation_milestones', {
  id: serial('id').primaryKey(),
  installationId: integer('installation_id').references(() => installations.id),
  milestoneName: text('milestone_name').notNull(),
  description: text('description'),
  status: text('status').default('pending'),
  completedAt: timestamp('completed_at'),
  createdAt: timestamp('created_at').defaultNow(),
});

// Battery serial numbers table
export const batterySerialNumbers = pgTable('battery_serial_numbers', {
  id: serial('id').primaryKey(),
  installationId: integer('installation_id').references(() => installations.id),
  serialNumber: text('serial_number').notNull(),
  batteryModel: text('battery_model'),
  createdAt: timestamp('created_at').defaultNow(),
});

// App settings table
export const appSettings = pgTable('app_settings', {
  id: serial('id').primaryKey(),
  key: text('key').unique().notNull(),
  value: jsonb('value'),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// Letters of authority table
export const lettersOfAuthority = pgTable('letters_of_authority', {
  id: serial('id').primaryKey(),
  customerId: integer('customer_id').references(() => customers.id),
  proposalId: integer('proposal_id').references(() => proposals.id),
  authorizedPersonName: text('authorized_person_name').notNull(),
  authorizedPersonTitle: text('authorized_person_title'),
  companyName: text('company_name').notNull(),
  companyAddress: text('company_address').notNull(),
  signatureImage: text('signature_image'),
  status: text('status').default('pending'),
  accessToken: text('access_token'),
  customerEmail: text('customer_email'),
  mpan: text('mpan'),
  supplierName: text('supplier_name'),
  dateOfSigning: timestamp('date_of_signing'),
  sentAt: timestamp('sent_at'),
  customerType: text('customer_type').default('residential'),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// Completion of works record table
export const completionOfWorksRecord = pgTable('completion_of_works_record', {
  id: serial('id').primaryKey(),
  installationId: integer('installation_id').references(() => installations.id),
  customerId: integer('customer_id').references(() => customers.id),
  customerName: text('customer_name').notNull(),
  customerAddress: text('customer_address').notNull(),
  customerEmail: text('customer_email'),
  customerPhone: text('customer_phone'),
  projectRefNumber: text('project_ref_number').notNull(),
  installationAddress: text('installation_address').notNull(),
  installationDescription: text('installation_description').notNull(),
  complianceCertificate: text('compliance_certificate'),
  mcsComplianceCertificate: text('mcs_compliance_certificate'),
  warrantyDocuments: text('warranty_documents'),
  operationManuals: text('operation_manuals'),
  maintenanceSchedule: text('maintenance_schedule'),

  // HIES Checklist fields
  confirmationBuildingRegs: boolean('confirmation_building_regs').default(false),
  manufacturersManuals: boolean('manufacturers_manuals').default(false),
  serviceRequirements: boolean('service_requirements').default(false),
  maintenanceRequirements: boolean('maintenance_requirements').default(false),
  performanceEstimates: boolean('performance_estimates').default(false),
  mcsCertificate: boolean('mcs_certificate').default(false),
  mcsComplianceConfirmation: boolean('mcs_compliance_confirmation').default(false),

  // System Components (consolidated fields auto-populated from site survey)
  panelDetails: text('panel_details'),
  inverter1Details: text('inverter1_details'),
  inverter2Details: text('inverter2_details'),
  inverter3Details: text('inverter3_details'),
  batteryDetails: text('battery_details'),

  // Additional Components
  optimiserDetails: text('optimiser_details'),
  evChargerDetails: text('ev_charger_details'),
  hotWaterDetails: text('hot_water_details'),
  atsEpsDetails: text('ats_eps_details'),
  birdGuardDetails: text('bird_guard_details'),
  monitoringDetails: text('monitoring_details'),
  otherDetails: text('other_details'),

  // Customer feedback
  customerComments: text('customer_comments'),

  // Signature fields
  signatureImage: text('signature_image'), // Base64 encoded signature
  signedByName: text('signed_by_name'),
  signedAt: timestamp('signed_at'),

  // Status and access control
  status: text('status').default('draft'), // draft, pending, signed, completed
  accessToken: text('access_token'),
  signingToken: text('signing_token'), // Secure token for signing access
  tokenExpiresAt: timestamp('token_expires_at'),

  // Communication tracking
  sentAt: timestamp('sent_at'),
  dateOfSigning: date('date_of_signing'),

  // Audit fields
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// Handover documents table
export const handoverDocuments = pgTable('handover_documents', {
  id: serial('id').primaryKey(),
  customerId: integer('customer_id').references(() => customers.id),
  installationId: integer('installation_id').references(() => installations.id),
  customerName: text('customer_name').notNull(),
  customerAddress: text('customer_address').notNull(),
  projectRefNumber: text('project_ref_number').notNull(),
  installationAddress: text('installation_address').notNull(),
  installationDescription: text('installation_description').notNull(),
  complianceCertificate: text('compliance_certificate'),
  warrantyDocuments: text('warranty_documents'),
  operationManuals: text('operation_manuals'),
  maintenanceSchedule: text('maintenance_schedule'),
  status: text('status').default('pending'),
  accessToken: text('access_token'),
  customerEmail: text('customer_email'),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// Site surveys table
export const siteSurveys = pgTable('site_surveys', {
  id: serial('id').primaryKey(),
  customerId: integer('customer_id')
    .notNull()
    .references(() => customers.id),
  installationId: integer('installation_id')
    .references(() => installations.id),

  // Survey Header Information
  siteAssessorName: text('site_assessor_name'),
  surveyDate: date('survey_date'),

  // Client Information
  clientName: text('client_name'),
  clientAddress: text('client_address'),
  clientPostCode: text('client_post_code'),
  clientTelephone: text('client_telephone'),
  clientEmail: text('client_email'),
  clientCity: text('client_city'),

  // Project Details
  panelsDescription: text('panels_description'),
  panelsQuantity: integer('panels_quantity'),
  // Dynamic panels array - each entry has { id, description, quantity, highlighted }
  panels: jsonb('panels').default([]),
  inverter1: text('inverter1'),
  inverter2: text('inverter2'),
  inverter3: text('inverter3'),
  batteryDescription: text('battery_description'),
  batteryQuantity: integer('battery_quantity'),
  // Dynamic batteries array - each entry has { id, description, quantity, highlighted }
  batteries: jsonb('batteries').default([]),

  // Property Details
  propertyType: text('property_type'),

  // Panel Dimensions (survey-specific overrides)
  panelDimensions: jsonb('panel_dimensions'),

  // Photos now stored in separate site_survey_photos table

  // Roof Information
  roofs: jsonb('roofs').default([]),
  scopeOfWorksRoof: text('scope_of_works_roof'),
  scaffolding: text('scaffolding'),
  scaffoldingWidth: numeric('scaffolding_width', { precision: 5, scale: 2 }),
  scaffoldingLifts: integer('scaffolding_lifts'),
  scaffoldingNotes: text('scaffolding_notes'),
  // scaffoldPlanImage moved to site_survey_photos table

  // Electric Information
  phases: text('phases'),
  earthingSystem: text('earthing_system'),
  bondingWater: text('bonding_water'), // Changed from boolean to text to support "YES", "N/A", "REQUIRED"
  bondingGas: text('bonding_gas'), // Changed from boolean to text to support "YES", "N/A", "REQUIRED"
  mainFuseSize: text('main_fuse_size'),
  existingDemand: text('existing_demand'), // AC demand in Amps
  newDemand: text('new_demand'), // AC demand in Amps after solar installation
  acSpdPresent: text('ac_spd_present'), // Changed from boolean to text to support "YES", "NO", "REQUIRED"
  eicr: text('eicr'), // Changed from boolean to text to support "NOT REQUIRED", "REQUIRED"
  spaceAvailableForPvDb: text('space_available_for_pv_db'),

  // Electric photos moved to site_survey_photos table

  // Installation Information - AC Installation
  inverterDb: text('inverter_db'),
  acSize: text('ac_size'),
  switchFuseRequired: text('switch_fuse_required'),
  exportLimitation: text('export_limitation'),
  inverterCircuits: jsonb('inverter_circuits').default([]),
  // cableRouteImages moved to site_survey_photos table

  // Equipment Location Information
  proposedArea: text('proposed_area'),
  proposedAreaNotes: text('proposed_area_notes'),
  // proposedAreaPhoto moved to site_survey_photos table
  overboardWall: text('overboard_wall'),
  canopy: text('canopy'),
  equipmentHeight: text('equipment_height'),
  equipmentWidth: text('equipment_width'),
  equipmentNotes: text('equipment_notes'),
  scopeOfWorksInverterAC: text('scope_of_works_inverter_ac'),

  // DC & Additional Works
  dcStrings: text('dc_strings'),
  dcCableCsa: text('dc_cable_csa'),
  dcLongestRun: text('dc_longest_run'),
  // dcRouteImages moved to site_survey_photos table
  scopeOfWorksDcRuns: text('scope_of_works_dc_runs'),

  // Additional Works
  evChargerRequired: boolean('ev_charger_required').default(false),
  evChargerDetails: text('ev_charger_details'),
  immersionCircuitRequired: boolean('immersion_circuit_required').default(false),
  immersionCircuitDetails: text('immersion_circuit_details'),
  birdguardRequired: boolean('birdguard_required').default(false),
  birdguardDetails: text('birdguard_details'),
  gatewayRequired: boolean('gateway_required').default(false),
  gatewayDetails: text('gateway_details'),

  // Earthing & Ground Works
  gatewayEarthing: text('gateway_earthing'),
  // earthPitLocationImage moved to site_survey_photos table
  groundTools: text('ground_tools'),
  diggerHire: text('digger_hire'),
  scopeOfWorksGroundWorks: text('scope_of_works_ground_works'),

  // GPS & Survey Location
  surveyLatitude: numeric('survey_latitude', { precision: 10, scale: 7 }),
  surveyLongitude: numeric('survey_longitude', { precision: 10, scale: 7 }),
  surveyAddress: text('survey_address'),

  // Special Requirements
  specialRequirements: text('special_requirements'),

  // Status and metadata fields
  status: text('status').default('draft'),
  lastActiveTab: text('last_active_tab'),
  // completedBy removed during database cleanup

  // Highlight metadata for tracking highlighted fields
  highlightedFields: jsonb('highlighted_fields').default('{}'),

  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// Site survey photos table
export const siteSurveyPhotos = pgTable('site_survey_photos', {
  id: serial('id').primaryKey(),
  siteSurveyId: integer('site_survey_id')
    .references(() => siteSurveys.id)
    .notNull(),
  photoType: text('photo_type').notNull(),
  filename: text('filename').notNull(),
  originalFilename: text('original_filename').notNull(),
  filePath: text('file_path').notNull(),
  fileSize: integer('file_size').notNull(),
  mimeType: text('mime_type').notNull(),
  description: text('description'),

  // GPS and metadata for photo
  photoLatitude: numeric('photo_latitude', { precision: 10, scale: 7 }),
  photoLongitude: numeric('photo_longitude', { precision: 10, scale: 7 }),
  photoTimestamp: timestamp('photo_timestamp'),

  // Annotation data (stored as JSON)
  annotations: jsonb('annotations').default('[]'),

  // Display order
  displayOrder: integer('display_order').default(0),

  // OneDrive integration fields
  oneDriveId: text('onedrive_id'),
  oneDrivePath: text('onedrive_path'),
  thumbnailUrl: text('thumbnail_url'),
  compressedUrl: text('compressed_url'),
  fullResolutionUrl: text('full_resolution_url'),

  // Upload status tracking
  uploadStatus: text('upload_status').default('pending'), // pending, uploading, completed, failed
  uploadProgress: integer('upload_progress').default(0), // 0-100
  uploadError: text('upload_error'),
  retryCount: integer('retry_count').default(0),

  // Performance optimization fields
  thumbnailSize: integer('thumbnail_size'),
  compressedSize: integer('compressed_size'),
  compressionRatio: numeric('compression_ratio', { precision: 5, scale: 2 }),

  // Caching and sync
  cachedAt: timestamp('cached_at'),
  syncedAt: timestamp('synced_at'),

  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// OpenSolar projects table
export const openSolarProjects = pgTable('opensolar_projects', {
  id: serial('id').primaryKey(),
  customerId: integer('customer_id').references(() => customers.id),
  externalId: text('external_id').unique().notNull(),
  name: text('name').notNull(),
  address: text('address'),
  status: text('status'),
  metadata: jsonb('metadata'),
  lastSyncedAt: timestamp('last_synced_at'),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// OpenSolar designs table
export const openSolarDesigns = pgTable('opensolar_designs', {
  id: serial('id').primaryKey(),
  projectId: integer('project_id').references(() => openSolarProjects.id),
  externalId: text('external_id').unique().notNull(),
  name: text('name').notNull(),
  status: text('status'),
  metadata: jsonb('metadata'),
  lastSyncedAt: timestamp('last_synced_at'),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// OpenSolar quotes table
export const openSolarQuotes = pgTable('opensolar_quotes', {
  id: serial('id').primaryKey(),
  designId: integer('design_id').references(() => openSolarDesigns.id),
  externalId: text('external_id').unique().notNull(),
  name: text('name').notNull(),
  status: text('status'),
  metadata: jsonb('metadata'),
  lastSyncedAt: timestamp('last_synced_at'),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// User preferences table
export const userPreferences = pgTable('user_preferences', {
  id: serial('id').primaryKey(),
  userId: integer('user_id').references(() => users.id),
  key: text('key').notNull(),
  value: jsonb('value'),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// Customer memories table
export const customerMemories = pgTable('customer_memories', {
  id: serial('id').primaryKey(),
  customerId: integer('customer_id').references(() => customers.id),
  memory: text('memory').notNull(),
  category: text('category'),
  importance: integer('importance').default(1),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// Project memories table
export const projectMemories = pgTable('project_memories', {
  id: serial('id').primaryKey(),
  projectId: integer('project_id'),
  memory: text('memory').notNull(),
  category: text('category'),
  importance: integer('importance').default(1),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// Business memory table
export const businessMemory = pgTable('business_memory', {
  id: serial('id').primaryKey(),
  memory: text('memory').notNull(),
  category: text('category'),
  importance: integer('importance').default(1),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// Knowledge nodes table
export const knowledgeNodes = pgTable('knowledge_nodes', {
  id: serial('id').primaryKey(),
  nodeId: text('node_id').unique().notNull(),
  content: text('content').notNull(),
  nodeType: text('node_type'),
  metadata: jsonb('metadata'),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// Knowledge relationships table
export const knowledgeRelationships = pgTable('knowledge_relationships', {
  id: serial('id').primaryKey(),
  sourceNodeId: text('source_node_id').notNull(),
  targetNodeId: text('target_node_id').notNull(),
  relationshipType: text('relationship_type'),
  weight: real('weight').default(1.0),
  metadata: jsonb('metadata'),
  createdAt: timestamp('created_at').defaultNow(),
});

// AI conversation context table
export const aiConversationContext = pgTable('ai_conversation_context', {
  id: serial('id').primaryKey(),
  sessionId: text('session_id').notNull(),
  context: jsonb('context'),
  lastUpdated: timestamp('last_updated').defaultNow(),
});

// Email conversations table
export const emailConversations = pgTable('email_conversations', {
  id: serial('id').primaryKey(),
  customer_id: integer('customer_id').references(() => customers.id),
  thread_id: text('thread_id').unique().notNull(),
  subject: text('subject').notNull(),
  participants: jsonb('participants'), // Array of email addresses
  last_message_at: timestamp('last_message_at'),
  message_count: integer('message_count').default(0),
  status: text('status').default('active'), // active, archived, deleted
  created_at: timestamp('created_at').defaultNow(),
  updated_at: timestamp('updated_at').defaultNow(),
});

// Email messages table
export const emailMessages = pgTable('email_messages', {
  id: serial('id').primaryKey(),
  conversation_id: integer('conversation_id').references(() => emailConversations.id),
  microsoft_message_id: text('microsoft_message_id').unique().notNull(),
  sender_email: text('sender_email').notNull(),
  sender_name: text('sender_name'),
  recipient_emails: jsonb('recipient_emails').notNull(),
  cc_emails: jsonb('cc_emails'),
  bcc_emails: jsonb('bcc_emails'),
  subject: text('subject').notNull(),
  body_preview: text('body_preview'),
  body_content: text('body_content'),
  body_content_type: text('body_content_type').default('html'), // html, text
  importance: text('importance').default('normal'), // low, normal, high
  is_read: boolean('is_read').default(false),
  is_sent_by_crm: boolean('is_sent_by_crm').default(false),
  has_attachments: boolean('has_attachments').default(false),
  received_at: timestamp('received_at').notNull(),
  sent_at: timestamp('sent_at'),
  created_at: timestamp('created_at').defaultNow(),
  updated_at: timestamp('updated_at').defaultNow(),
});

// Email attachments table
export const emailAttachments = pgTable('email_attachments', {
  id: serial('id').primaryKey(),
  message_id: integer('message_id').references(() => emailMessages.id),
  microsoft_attachment_id: text('microsoft_attachment_id').notNull(),
  filename: text('filename').notNull(),
  content_type: text('content_type'),
  size_bytes: integer('size_bytes'),
  storage_path: text('storage_path'), // Path in Replit Object Storage
  is_inline: boolean('is_inline').default(false),
  created_at: timestamp('created_at').defaultNow(),
});

// Email sync status table
export const emailSyncStatus = pgTable('email_sync_status', {
  id: serial('id').primaryKey(),
  mailbox_email: text('mailbox_email').unique().notNull(),
  last_sync_at: timestamp('last_sync_at'),
  last_message_id: text('last_message_id'),
  sync_token: text('sync_token'), // Microsoft Graph delta token
  status: text('status').default('active'), // active, error, paused
  error_message: text('error_message'),
  created_at: timestamp('created_at').defaultNow(),
  updated_at: timestamp('updated_at').defaultNow(),
});

// All company emails table (matched and unmatched)
export const allCompanyEmails = pgTable('all_company_emails', {
  id: serial('id').primaryKey(),
  microsoft_message_id: text('microsoft_message_id').unique().notNull(),
  mailbox: text('mailbox').notNull(), // Which company mailbox this came from
  thread_id: text('thread_id').notNull(),
  subject: text('subject').notNull(),
  sender_email: text('sender_email').notNull(),
  sender_name: text('sender_name'),
  recipients: jsonb('recipients'), // All recipients (to, cc, bcc)
  body_preview: text('body_preview'),
  received_at: timestamp('received_at').notNull(),
  has_attachments: boolean('has_attachments').default(false),
  customer_id: integer('customer_id').references(() => customers.id), // NULL if unmatched
  customer_match_method: text('customer_match_method'), // exact, fuzzy, manual, null
  is_internal: boolean('is_internal').default(false), // Internal company email
  created_at: timestamp('created_at').defaultNow(),
  updated_at: timestamp('updated_at').defaultNow(),
});

// Email signatures table
export const emailSignatures = pgTable('email_signatures', {
  id: serial('id').primaryKey(),
  user_id: integer('user_id').references(() => users.id),
  signature_name: text('signature_name').notNull(),
  signature_html: text('signature_html').notNull(),
  signature_text: text('signature_text').notNull(),
  is_default: boolean('is_default').default(false),
  is_active: boolean('is_active').default(true),
  created_at: timestamp('created_at').defaultNow(),
  updated_at: timestamp('updated_at').defaultNow(),
});

// User email settings table
export const userEmailSettings = pgTable('user_email_settings', {
  id: serial('id').primaryKey(),
  user_id: integer('user_id')
    .references(() => users.id)
    .unique(),
  default_signature_id: integer('default_signature_id').references(() => emailSignatures.id),
  auto_add_signature: boolean('auto_add_signature').default(true),
  reply_signature_id: integer('reply_signature_id').references(() => emailSignatures.id),
  microsoft_access_token: text('microsoft_access_token'),
  microsoft_refresh_token: text('microsoft_refresh_token'),
  microsoft_account_id: text('microsoft_account_id'),
  microsoft_token_expires_at: timestamp('microsoft_token_expires_at'),
  created_at: timestamp('created_at').defaultNow(),
  updated_at: timestamp('updated_at').defaultNow(),
});

// Customer activities table
export const customerActivities = pgTable('customer_activities', {
  id: serial('id').primaryKey(),
  customerId: integer('customer_id').references(() => customers.id),
  activityType: text('activity_type').notNull(),
  summary: text('summary'),
  content: text('content'),
  contentLink: text('content_link'),
  twilioCallSid: text('twilio_call_sid'),
  callDuration: integer('call_duration'),
  metadata: jsonb('metadata'),
  createdBy: integer('created_by').references(() => users.id),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// Email templates table
export const emailTemplates = pgTable('email_templates', {
  id: serial('id').primaryKey(),
  name: text('name').notNull(),
  subject: text('subject').notNull(),
  htmlContent: text('html_content').notNull(),
  textContent: text('text_content'),
  category: text('category'),
  tags: text('tags'), // JSON string of tags
  isFavorite: boolean('is_favorite').default(false),
  usageCount: integer('usage_count').default(0),
  lastUsed: timestamp('last_used'),
  isActive: boolean('is_active').default(true),
  createdBy: integer('created_by').references(() => users.id),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// Email drafts table
export const emailDrafts = pgTable('email_drafts', {
  id: serial('id').primaryKey(),
  userId: integer('user_id').references(() => users.id),
  customerId: integer('customer_id').references(() => customers.id),
  conversationId: integer('conversation_id').references(() => emailConversations.id),
  subject: text('subject'),
  bodyHtml: text('body_html'),
  bodyText: text('body_text'),
  recipients: text('recipients'), // JSON string of email addresses
  cc: text('cc'), // JSON string of email addresses
  bcc: text('bcc'), // JSON string of email addresses
  attachments: text('attachments'), // JSON string of attachment info
  signatureId: integer('signature_id').references(() => emailSignatures.id),
  isDraft: boolean('is_draft').default(true),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// Meta conversions config table
export const metaConversionsConfig = pgTable('meta_conversions_config', {
  id: serial('id').primaryKey(),
  pixelId: text('pixel_id').notNull(),
  accessToken: text('access_token').notNull(),
  testEventCode: text('test_event_code'),
  isActive: boolean('is_active').default(true),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// OpenSolar settings table
export const openSolarSettings = pgTable('opensolar_settings', {
  id: serial('id').primaryKey(),
  apiKey: text('api_key').notNull(),
  email: text('email').notNull(),
  orgId: text('org_id').notNull(),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// OpenSolar events table
export const openSolarEvents = pgTable('opensolar_events', {
  id: serial('id').primaryKey(),
  eventType: text('event_type').notNull(),
  payload: jsonb('payload'),
  processedAt: timestamp('processed_at'),
  createdAt: timestamp('created_at').defaultNow(),
});

// Webhook logs table
export const webhookLogs = pgTable('webhook_logs', {
  id: serial('id').primaryKey(),
  source: text('source').notNull(),
  eventType: text('event_type'),
  payload: jsonb('payload'),
  statusCode: integer('status_code'),
  response: text('response'),
  createdAt: timestamp('created_at').defaultNow(),
});

// Consent records table for GDPR compliance
export const consentRecords = pgTable('consent_records', {
  id: serial('id').primaryKey(),
  userId: integer('user_id').references(() => users.id),
  sessionId: text('session_id'),
  ipAddress: text('ip_address'),
  categories: jsonb('categories').notNull(), // {necessary: true, analytics: false, marketing: true, preferences: false}
  consentVersion: text('consent_version').notNull(),
  consentedAt: timestamp('consented_at').defaultNow().notNull(),
  revokedAt: timestamp('revoked_at'),
  userAgent: text('user_agent'),
  sourceUrl: text('source_url'),
});

// Mentions table for @mention functionality
export const mentions = pgTable('mentions', {
  id: serial('id').primaryKey(),
  mentionedUserId: integer('mentioned_user_id').notNull().references(() => users.id),
  mentionedByUserId: integer('mentioned_by_user_id').notNull().references(() => users.id),
  entityType: text('entity_type').notNull(), // 'customer', 'task', 'note', etc.
  entityId: integer('entity_id').notNull(),
  context: text('context'), // The text around the mention
  isRead: boolean('is_read').default(false),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// Tasks table for task assignment
export const tasks = pgTable('tasks', {
  id: serial('id').primaryKey(),
  title: text('title').notNull(),
  description: text('description'),
  customerId: integer('customer_id').references(() => customers.id),
  assignedTo: integer('assigned_to').references(() => users.id),
  assignedBy: integer('assigned_by').notNull().references(() => users.id),
  status: text('status').default('pending'), // 'pending', 'in_progress', 'completed', 'cancelled'
  priority: text('priority').default('medium'), // 'low', 'medium', 'high', 'urgent'
  dueDate: timestamp('due_date'),
  completedAt: timestamp('completed_at'),
  completedBy: integer('completed_by').references(() => users.id),
  pipelineStageId: integer('pipeline_stage_id').references(() => pipelineStages.id),
  tags: text('tags').$type<string[]>(), // Array of tags stored as JSON
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// Task comments table for task discussions
export const taskComments = pgTable('task_comments', {
  id: serial('id').primaryKey(),
  taskId: integer('task_id').notNull().references(() => tasks.id),
  userId: integer('user_id').notNull().references(() => users.id),
  comment: text('comment').notNull(),
  hasMentions: boolean('has_mentions').default(false),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// Notifications table
export const notifications = pgTable('notifications', {
  id: serial('id').primaryKey(),
  userId: integer('user_id').notNull().references(() => users.id),
  type: text('type').notNull(), // 'mention', 'task_assigned', 'task_completed', 'comment', etc.
  title: text('title').notNull(),
  message: text('message').notNull(),
  entityType: text('entity_type'), // 'customer', 'task', 'comment', etc.
  entityId: integer('entity_id'),
  isRead: boolean('is_read').default(false),
  isEmailSent: boolean('is_email_sent').default(false),
  actionUrl: text('action_url'), // URL to navigate to when notification is clicked
  createdBy: integer('created_by').references(() => users.id),
  createdAt: timestamp('created_at').defaultNow(),
  readAt: timestamp('read_at'),
});

// Relations
export const customersRelations = relations(customers, ({ many }) => ({
  proposals: many(proposals),
  installations: many(installations),
  documents: many(documentFiles),
  surveys: many(siteSurveys),
  activities: many(customerActivities),
}));

export const proposalsRelations = relations(proposals, ({ one, many }) => ({
  customer: one(customers, {
    fields: [proposals.customerId],
    references: [customers.id],
  }),
  installations: many(installations),
}));

export const installationsRelations = relations(installations, ({ one }) => ({
  customer: one(customers, {
    fields: [installations.customerId],
    references: [customers.id],
  }),
  proposal: one(proposals, {
    fields: [installations.proposalId],
    references: [proposals.id],
  }),
}));

export const leadsRelations = relations(leads, ({ one }) => ({
  pipelineStage: one(pipelineStages, {
    fields: [leads.pipelineStageId],
    references: [pipelineStages.id],
  }),
}));

// Relations for mentions system
export const mentionsRelations = relations(mentions, ({ one }) => ({
  mentionedUser: one(users, {
    fields: [mentions.mentionedUserId],
    references: [users.id],
    relationName: 'mentionedUser',
  }),
  mentionedBy: one(users, {
    fields: [mentions.mentionedByUserId],
    references: [users.id],
    relationName: 'mentionedBy',
  }),
}));

// Relations for tasks system
export const tasksRelations = relations(tasks, ({ one, many }) => ({
  customer: one(customers, {
    fields: [tasks.customerId],
    references: [customers.id],
  }),
  assignedToUser: one(users, {
    fields: [tasks.assignedTo],
    references: [users.id],
    relationName: 'assignedTasks',
  }),
  assignedByUser: one(users, {
    fields: [tasks.assignedBy],
    references: [users.id],
    relationName: 'createdTasks',
  }),
  completedByUser: one(users, {
    fields: [tasks.completedBy],
    references: [users.id],
    relationName: 'completedTasks',
  }),
  pipelineStage: one(pipelineStages, {
    fields: [tasks.pipelineStageId],
    references: [pipelineStages.id],
  }),
  comments: many(taskComments),
}));

// Relations for task comments
export const taskCommentsRelations = relations(taskComments, ({ one }) => ({
  task: one(tasks, {
    fields: [taskComments.taskId],
    references: [tasks.id],
  }),
  user: one(users, {
    fields: [taskComments.userId],
    references: [users.id],
  }),
}));

// Relations for notifications
export const notificationsRelations = relations(notifications, ({ one }) => ({
  user: one(users, {
    fields: [notifications.userId],
    references: [users.id],
  }),
  createdByUser: one(users, {
    fields: [notifications.createdBy],
    references: [users.id],
    relationName: 'createdNotifications',
  }),
}));

// Fix C: Relations for customer activities (enables `with: { createdByUser: true }` in queries)
export const customerActivitiesRelations = relations(customerActivities, ({ one }) => ({
  customer: one(customers, {
    fields: [customerActivities.customerId],
    references: [customers.id],
  }),
  createdByUser: one(users, {
    fields: [customerActivities.createdBy],
    references: [users.id],
    relationName: 'createdActivities',
  }),
}));

// Update users relations to include new tables
export const usersRelations = relations(users, ({ many }) => ({
  mentionsReceived: many(mentions, {
    relationName: 'mentionedUser',
  }),
  mentionsSent: many(mentions, {
    relationName: 'mentionedBy',
  }),
  assignedTasks: many(tasks, {
    relationName: 'assignedTasks',
  }),
  createdTasks: many(tasks, {
    relationName: 'createdTasks',
  }),
  completedTasks: many(tasks, {
    relationName: 'completedTasks',
  }),
  taskComments: many(taskComments),
  notifications: many(notifications),
  createdNotifications: many(notifications, {
    relationName: 'createdNotifications',
  }),
  createdActivities: many(customerActivities, {
    relationName: 'createdActivities',
  }),
}));

// Zod validation schemas
// Base insert schemas from Drizzle
export const insertUserSchema = createInsertSchema(users);
export const insertCustomerSchema = createInsertSchema(customers);
export const insertLeadSchema = createInsertSchema(leads);
export const insertProposalSchema = createInsertSchema(proposals);
export const insertInstallationSchema = createInsertSchema(installations);
export const insertDocumentFileSchema = createInsertSchema(documentFiles);
export const insertPipelineStageSchema = createInsertSchema(pipelineStages);
export const insertComponentSchema = createInsertSchema(components);
export const insertProposalItemSchema = createInsertSchema(proposalItems);
export const insertInstallationMilestoneSchema = createInsertSchema(installationMilestones);
export const insertBatterySerialNumberSchema = createInsertSchema(batterySerialNumbers);
export const insertCompletionOfWorksRecordSchema = createInsertSchema(completionOfWorksRecord);
export const insertLetterOfAuthoritySchema = createInsertSchema(lettersOfAuthority);
export const insertSiteSurveySchema = createInsertSchema(siteSurveys);

// Enhanced site survey form schema that properly maps frontend camelCase to database snake_case
export const siteSurveyFormSchema = insertSiteSurveySchema
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    // Ensure critical fields use proper validation for frontend
    acSpdPresent: z.string().nullable().optional(), // Validates as string: "YES", "NO", "REQUIRED"
    dateBooked: z.string().nullable().optional(), // ISO date string from DatePicker
    startTime: z.string().nullable().optional(), // Time string from form input

    // Ensure all other string fields are properly validated
    phases: z.string().nullable().optional(),
    earthingSystem: z.string().nullable().optional(),
    zsAtSocket: z.string().nullable().optional(),
    bondingWater: z.string().nullable().optional(),
    bondingGas: z.string().nullable().optional(),
    meterType: z.string().nullable().optional(),
    mainFuseSize: z.string().nullable().optional(),
    existingDemand: z.string().nullable().optional(),
    newDemand: z.string().nullable().optional(),
    eicr: z.string().nullable().optional(),

    // Equipment and location fields
    inverterDb: z.string().nullable().optional(),
    acSize: z.string().nullable().optional(),
    switchFuseRequired: z.string().nullable().optional(),
    proposedArea: z.string().nullable().optional(),
    proposedAreaNotes: z.string().nullable().optional(),

    // Roof information fields
    scaffolding: z.string().nullable().optional(),
    scaffoldingNotes: z.string().nullable().optional(),

    // Ensure arrays are properly validated
    panels: z.array(z.any()).default([]),
    roofs: z.array(z.any()).default([]),
    inverterCircuits: z.array(z.any()).default([]),
    
    // Highlight metadata for tracking highlighted fields in forms (not stored in DB)
    highlightedFields: z.record(z.boolean()).optional().default({}),
  });
export const insertOpenSolarProjectSchema = createInsertSchema(openSolarProjects);
export const insertOpenSolarDesignSchema = createInsertSchema(openSolarDesigns);
export const insertOpenSolarQuoteSchema = createInsertSchema(openSolarQuotes);
export const insertUserPreferenceSchema = createInsertSchema(userPreferences);
export const insertCustomerMemorySchema = createInsertSchema(customerMemories);
export const insertProjectMemorySchema = createInsertSchema(projectMemories);
export const insertBusinessMemorySchema = createInsertSchema(businessMemory);
export const insertKnowledgeNodeSchema = createInsertSchema(knowledgeNodes);
export const insertKnowledgeRelationshipSchema = createInsertSchema(knowledgeRelationships);
export const insertCustomerActivitySchema = createInsertSchema(customerActivities);
export const insertEmailTemplateSchema = createInsertSchema(emailTemplates);
export const insertMetaConversionsConfigSchema = createInsertSchema(metaConversionsConfig);
export const insertOpenSolarSettingsSchema = createInsertSchema(openSolarSettings);
export const insertOpenSolarEventSchema = createInsertSchema(openSolarEvents);
export const insertWebhookLogSchema = createInsertSchema(webhookLogs);
export const insertMentionSchema = createInsertSchema(mentions);
export const insertTaskSchema = createInsertSchema(tasks);
export const insertTaskCommentSchema = createInsertSchema(taskComments);
export const insertNotificationSchema = createInsertSchema(notifications);

// Enhanced installation form schema that matches frontend form requirements
export const installationFormSchema = insertInstallationSchema
  .omit({
    commissioningDate: true, // Remove DB field to avoid conflicts
  })
  .extend({
    // Handle field name mapping for backward compatibility
    installationAddress: z.string().nullable().optional(), // Maps to 'installation_address' in DB

    // Additional frontend-only fields for better UX
    customerEmail: z.string().nullable().optional(),
    customerPhone: z.string().nullable().optional(),

    // Critical fix: Map camelCase form fields to snake_case DB fields
    commissioningDate: z.date().nullable().optional(), // Frontend field that maps to commissioning_date in DB

    // Ensure all date fields handle null/undefined consistently
    contractSigned: z.date().nullable().optional(),
    scheduledDate: z.date().nullable().optional(),
    completedDate: z.date().nullable().optional(),
    depositPaid: z.date().nullable().optional(),
    sevenDayPaymentDate: z.date().nullable().optional(),
    finalPaymentDate: z.date().nullable().optional(),
    additionalPaymentDate: z.date().nullable().optional(),
    dnoApplicationSent: z.date().nullable().optional(),
    dnoApproved: z.date().nullable().optional(),
  })
  .extend({
    // Enhanced document arrays with proper typing
    financeDocuments: z
      .array(
        z.object({
          data: z.string(),
          name: z.string(),
          type: z.string(),
          size: z.number(),
        })
      )
      .default([]),

    dnoDocuments: z
      .array(
        z.object({
          data: z.string(),
          name: z.string(),
          type: z.string(),
          size: z.number(),
        })
      )
      .default([]),

    onsiteDocuments: z
      .array(
        z.object({
          data: z.string(),
          name: z.string(),
          type: z.string(),
          size: z.number(),
        })
      )
      .default([]),

    postInstallDocuments: z
      .array(
        z.object({
          data: z.string(),
          name: z.string(),
          type: z.string(),
          size: z.number(),
        })
      )
      .default([]),

    handoverDocuments: z
      .array(
        z.object({
          data: z.string(),
          name: z.string(),
          type: z.string(),
          size: z.number(),
        })
      )
      .default([]),

    maintainingDocuments: z
      .array(
        z.object({
          data: z.string(),
          name: z.string(),
          type: z.string(),
          size: z.number(),
        })
      )
      .default([]),

    technicalDocuments: z
      .array(
        z.object({
          data: z.string(),
          name: z.string(),
          type: z.string(),
          size: z.number(),
        })
      )
      .default([]),

    contractDocuments: z
      .array(
        z.object({
          data: z.string(),
          name: z.string(),
          type: z.string(),
          size: z.number(),
        })
      )
      .default([]),

    // DNO fees as structured array
    dnoFees: z
      .array(
        z.object({
          description: z.string(),
          amount: z.string(),
        })
      )
      .default([]),

    // Equipment list as structured array
    equipmentList: z
      .array(
        z.object({
          name: z.string(),
          model: z.string().optional(),
          quantity: z.number().optional(),
          serialNumber: z.string().optional(),
        })
      )
      .default([]),
  })
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  });

// Installation form types for frontend
export type InstallationFormData = z.infer<typeof installationFormSchema>;

// Site survey form types for frontend
export type SiteSurveyFormData = z.infer<typeof siteSurveyFormSchema>;

// Type definitions
export type User = typeof users.$inferSelect;
export type InsertUser = typeof users.$inferInsert;

export type Customer = typeof customers.$inferSelect;
export type InsertCustomer = typeof customers.$inferInsert;

export type Lead = typeof leads.$inferSelect;
export type InsertLead = typeof leads.$inferInsert;

export type Proposal = typeof proposals.$inferSelect;
export type InsertProposal = typeof proposals.$inferInsert;

export type Installation = typeof installations.$inferSelect;
export type InsertInstallation = typeof installations.$inferInsert;

export type DocumentFile = typeof documentFiles.$inferSelect;
export type InsertDocumentFile = typeof documentFiles.$inferInsert;

// N-1 in-memory fixtures predate tracking_key; keep their object shape valid
// while database reads can still return the nullable field.
export type PipelineStage = Omit<typeof pipelineStages.$inferSelect, 'trackingKey'> & {
  trackingKey?: string | null;
};
export type InsertPipelineStage = typeof pipelineStages.$inferInsert;

export type Component = typeof components.$inferSelect;
export type InsertComponent = typeof components.$inferInsert;

export type ProposalItem = typeof proposalItems.$inferSelect;
export type InsertProposalItem = typeof proposalItems.$inferInsert;

export type InstallationMilestone = typeof installationMilestones.$inferSelect;
export type InsertInstallationMilestone = typeof installationMilestones.$inferInsert;

export type BatterySerialNumber = typeof batterySerialNumbers.$inferSelect;
export type InsertBatterySerialNumber = typeof batterySerialNumbers.$inferInsert;

export type CompletionOfWorksRecord = typeof completionOfWorksRecord.$inferSelect;
export type InsertCompletionOfWorksRecord = typeof completionOfWorksRecord.$inferInsert;

export type LetterOfAuthority = typeof lettersOfAuthority.$inferSelect;
/** List view of an LOA: everything except the signature image, plus whether one exists. */
export type LetterOfAuthoritySummary = Omit<LetterOfAuthority, 'signatureImage'> & {
  hasSignature: boolean;
};
export type InsertLetterOfAuthority = typeof lettersOfAuthority.$inferInsert;

export type SiteSurvey = typeof siteSurveys.$inferSelect;
export type InsertSiteSurvey = typeof siteSurveys.$inferInsert;

export type SiteSurveyPhoto = typeof siteSurveyPhotos.$inferSelect;
export type InsertSiteSurveyPhoto = typeof siteSurveyPhotos.$inferInsert;

export type OpenSolarProject = typeof openSolarProjects.$inferSelect;
export type InsertOpenSolarProject = typeof openSolarProjects.$inferInsert;

export type OpenSolarDesign = typeof openSolarDesigns.$inferSelect;
export type InsertOpenSolarDesign = typeof openSolarDesigns.$inferInsert;

export type OpenSolarQuote = typeof openSolarQuotes.$inferSelect;
export type InsertOpenSolarQuote = typeof openSolarQuotes.$inferInsert;

export type UserPreference = typeof userPreferences.$inferSelect;
export type InsertUserPreference = typeof userPreferences.$inferInsert;

export type CustomerMemory = typeof customerMemories.$inferSelect;
export type InsertCustomerMemory = typeof customerMemories.$inferInsert;

export type ProjectMemory = typeof projectMemories.$inferSelect;
export type InsertProjectMemory = typeof projectMemories.$inferInsert;

export type BusinessMemory = typeof businessMemory.$inferSelect;
export type InsertBusinessMemory = typeof businessMemory.$inferInsert;

export type KnowledgeNode = typeof knowledgeNodes.$inferSelect;
export type InsertKnowledgeNode = typeof knowledgeNodes.$inferInsert;

export type KnowledgeRelationship = typeof knowledgeRelationships.$inferSelect;
export type InsertKnowledgeRelationship = typeof knowledgeRelationships.$inferInsert;

export type AIConversationContext = typeof aiConversationContext.$inferSelect;

export type CustomerActivity = typeof customerActivities.$inferSelect;
export type InsertCustomerActivity = typeof customerActivities.$inferInsert;

export type EmailTemplate = typeof emailTemplates.$inferSelect;
export type InsertEmailTemplate = typeof emailTemplates.$inferInsert;
export type EmailDraft = typeof emailDrafts.$inferSelect;
export type InsertEmailDraft = typeof emailDrafts.$inferInsert;

export type MetaConversionsConfig = typeof metaConversionsConfig.$inferSelect;
export type InsertMetaConversionsConfig = typeof metaConversionsConfig.$inferInsert;

export type OpenSolarSettings = typeof openSolarSettings.$inferSelect;
export type InsertOpenSolarSettings = typeof openSolarSettings.$inferInsert;

export type OpenSolarEvent = typeof openSolarEvents.$inferSelect;
export type InsertOpenSolarEvent = typeof openSolarEvents.$inferInsert;

export type WebhookLog = typeof webhookLogs.$inferSelect;
export type InsertWebhookLog = typeof webhookLogs.$inferInsert;

export type ConsentRecord = typeof consentRecords.$inferSelect;
export type InsertConsentRecord = typeof consentRecords.$inferInsert;

// Mention and task system types
export type Mention = typeof mentions.$inferSelect;
export type InsertMention = typeof mentions.$inferInsert;
export type Task = typeof tasks.$inferSelect;
export type InsertTask = typeof tasks.$inferInsert;
export type TaskComment = typeof taskComments.$inferSelect;
export type InsertTaskComment = typeof taskComments.$inferInsert;
export type Notification = typeof notifications.$inferSelect;
export type InsertNotification = typeof notifications.$inferInsert;

// Pagination types
export interface PaginationMetadata {
  page: number;
  limit: number;
  totalPages: number;
  totalCount: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: PaginationMetadata;
}

export interface CustomerFilters {
  search?: string;
  status?: string;
  pipelineStageId?: number;
  page?: number;
  limit?: number;
  fields?: string[];
}

// ---------------------
// New enterprise-grade integrations (add-only, safe)
// ---------------------

// Pipeline stage history (append-only) - Enhanced with tracking metrics
export const pipelineStageHistory = pgTable('pipeline_stage_history', {
  id: serial('id').primaryKey(),
  customerId: integer('customer_id').references(() => customers.id),
  fromStageId: integer('from_stage_id').references(() => pipelineStages.id),
  toStageId: integer('to_stage_id').references(() => pipelineStages.id),
  reasonId: integer('reason_id'),
  reason: text('reason'), // Text reason for movement
  notes: text('notes'),
  amountAtChange: integer('amount_at_change'),
  changedBy: integer('changed_by').references(() => users.id),
  durationInStage: integer('duration_in_stage'), // Time spent in previous stage (seconds)
  velocityScore: numeric('velocity_score', { precision: 5, scale: 2 }), // Calculated velocity score
  slaStatus: text('sla_status'), // 'on_time', 'warning', 'violated'
  automatedMove: boolean('automated_move').default(false), // Whether move was automated
  createdAt: timestamp('created_at').defaultNow(),
  transitionRequestId: text('transition_request_id'),
  requestDigest: text('request_digest'),
  occurrenceId: text('occurrence_id'),
  transitionSequence: integer('transition_sequence'),
  previousPipelineVersion: integer('previous_pipeline_version'),
  pipelineVersion: integer('pipeline_version'),
  fromStageKey: text('from_stage_key'),
  toStageKey: text('to_stage_key'),
  completionChannel: text('completion_channel').$type<TrackingCompletionChannel>(),
  actionSource: text('action_source').$type<TrackingMetaActionSource>(),
  actionSourceReason: text('action_source_reason'),
  transitionProvenance: text('transition_provenance'),
  sourceClassification: text('source_classification'),
}, (table) => [
  uniqueIndex('pipeline_stage_history_request_uq')
    .on(table.customerId, table.transitionRequestId)
    .where(sql`${table.transitionRequestId} is not null`),
  uniqueIndex('pipeline_stage_history_occurrence_uq')
    .on(table.occurrenceId)
    .where(sql`${table.occurrenceId} is not null`),
]);

// Pipeline Stage SLA Configuration
export const pipelineStageSLAs = pgTable('pipeline_stage_slas', {
  id: serial('id').primaryKey(),
  stageId: integer('stage_id').references(() => pipelineStages.id).notNull(),
  slaHours: integer('sla_hours').notNull(), // SLA duration in hours
  warningHours: integer('warning_hours'), // Warning threshold (e.g., 80% of SLA)
  actionType: text('action_type').notNull(), // 'email', 'notification', 'task', 'webhook'
  actionConfig: jsonb('action_config'), // Configuration for action (recipients, template, etc.)
  escalationHours: integer('escalation_hours'), // Hours before escalation
  escalationConfig: jsonb('escalation_config'), // Escalation settings
  businessHoursOnly: boolean('business_hours_only').default(true),
  isActive: boolean('is_active').default(true),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// Pipeline Analytics Cache
export const pipelineAnalytics = pgTable('pipeline_analytics', {
  id: serial('id').primaryKey(),
  metricType: text('metric_type').notNull(), // 'conversion', 'velocity', 'forecast', 'bottleneck'
  stageFrom: integer('stage_from').references(() => pipelineStages.id),
  stageTo: integer('stage_to').references(() => pipelineStages.id),
  period: text('period').notNull(), // 'daily', 'weekly', 'monthly', 'quarterly'
  periodStart: timestamp('period_start').notNull(),
  periodEnd: timestamp('period_end').notNull(),
  value: numeric('value', { precision: 10, scale: 2 }).notNull(),
  count: integer('count'), // Number of items in calculation
  metadata: jsonb('metadata'), // Additional metrics (min, max, std_dev, etc.)
  source: text('source'), // Filter by source if applicable
  agent: text('agent'), // Filter by agent if applicable
  calculatedAt: timestamp('calculated_at').defaultNow(),
});

// Pipeline Stage Reasons (for win/loss analysis)
export const pipelineStageReasons = pgTable('pipeline_stage_reasons', {
  id: serial('id').primaryKey(),
  stageId: integer('stage_id').references(() => pipelineStages.id),
  reason: text('reason').notNull(),
  reasonCategory: text('reason_category'), // 'price', 'competition', 'timing', 'product_fit', etc.
  isPositive: boolean('is_positive').default(false), // True for win reasons, false for loss
  displayOrder: integer('display_order').default(0),
  isActive: boolean('is_active').default(true),
  createdAt: timestamp('created_at').defaultNow(),
});

// Webhook subscriptions
export const webhookSubscriptions = pgTable('webhook_subscriptions', {
  id: serial('id').primaryKey(),
  targetUrl: text('target_url').notNull(),
  secret: text('secret'),
  filter: jsonb('filter'), // e.g., { type: 'pipeline.stage_changed', toStages: [8,9] }
  isActive: boolean('is_active').default(true),
  createdBy: integer('created_by'),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// Webhook events outbox
export const webhookEvents = pgTable('webhook_events', {
  id: text('id').primaryKey(), // UUID or hashed ID
  type: text('type').notNull(),
  payload: jsonb('payload').notNull(),
  status: text('status').default('pending'), // pending|delivering|delivered|failed
  attempts: integer('attempts').default(0),
  nextAttemptAt: timestamp('next_attempt_at'),
  createdAt: timestamp('created_at').defaultNow(),
});

// Webhook deliveries log
export const webhookDeliveries = pgTable('webhook_deliveries', {
  id: serial('id').primaryKey(),
  eventId: text('event_id').notNull(),
  subscriptionId: integer('subscription_id').notNull(),
  attemptNo: integer('attempt_no').default(1),
  status: text('status').notNull(), // success|failed
  responseCode: integer('response_code'),
  responseMs: integer('response_ms'),
  errorText: text('error_text'),
  createdAt: timestamp('created_at').defaultNow(),
});

// Meta Events table for deduplication and tracking
export const metaEvents = pgTable('meta_events', {
  id: serial('id').primaryKey(),
  eventId: text('event_id').notNull().unique(),
  eventName: text('event_name').notNull(),
  customerId: integer('customer_id').references(() => customers.id),
  leadId: text('lead_id'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  sentAt: timestamp('sent_at'),
  response: jsonb('response'),
  success: boolean('success').default(false),
  sourceOccurrenceId: text('source_occurrence_id'),
  tenantKey: text('tenant_key'),
  sourceObjectType: text('source_object_type'),
  sourceObjectVersion: text('source_object_version'),
  contractKey: text('contract_key'),
  projectionKey: text('projection_key'),
  destinationKey: text('destination_key'),
  producerContractVersion: text('producer_contract_version'),
  minimumDispatcherCapability: text('minimum_dispatcher_capability'),
  policyVersion: text('policy_version'),
  completionChannelVersion: text('completion_channel_version'),
  actionSourceMappingVersion: text('action_source_mapping_version'),
  configVersion: text('config_version'),
  configFingerprint: text('config_fingerprint'),
  eligibilitySnapshot: jsonb('eligibility_snapshot'),
  // Immutable hashed CRM match data, separate from semantic outcome evidence.
  // Classified personal data: scrub on erasure and never copy into receipts.
  protectedMatchEnvelope: jsonb('protected_match_envelope'),
  originalEventAt: timestamp('original_event_at', { withTimezone: true }),
  acquisitionSource: text('acquisition_source').$type<TrackingAcquisitionSource>(),
  completionChannel: text('completion_channel').$type<TrackingCompletionChannel>(),
  actionSource: text('action_source').$type<TrackingMetaActionSource>(),
  payloadDigest: text('payload_digest'),
  status: text('status').notNull().default('legacy_non_claimable'),
  claimable: boolean('claimable').notNull().default(false),
  attemptCount: integer('attempt_count').notNull().default(0),
  nextAttemptAt: timestamp('next_attempt_at', { withTimezone: true }),
  leaseToken: text('lease_token'),
  leaseOwner: text('lease_owner'),
  leaseExpiresAt: timestamp('lease_expires_at', { withTimezone: true }),
  recoveryOwner: text('recovery_owner'),
  suppressionEpochAtOccurrence: integer('suppression_epoch_at_occurrence'),
  subjectIdAtOccurrence: text('subject_id_at_occurrence'),
  authorizationIdAtOccurrence: text('authorization_id_at_occurrence'),
  lastFailureClass: text('last_failure_class'),
  sanitizedReceipt: jsonb('sanitized_receipt'),
  testEvent: boolean('test_event').notNull().default(false),
  updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  terminalAt: timestamp('terminal_at', { withTimezone: true }),
}, (table) => [
  uniqueIndex('meta_events_projection_key_uq')
    .on(table.projectionKey)
    .where(sql`${table.projectionKey} is not null`),
  uniqueIndex('meta_events_source_contract_uq')
    .on(
      table.tenantKey,
      table.sourceObjectType,
      table.sourceOccurrenceId,
      table.sourceObjectVersion,
      table.contractKey
    )
    .where(sql`${table.tenantKey} is not null and ${table.sourceObjectType} is not null and ${table.sourceOccurrenceId} is not null and ${table.sourceObjectVersion} is not null and ${table.contractKey} is not null`),
  index('meta_events_due_idx')
    .on(table.nextAttemptAt, table.createdAt, table.id)
    .where(sql`${table.claimable} and ${table.status} in ('queued', 'retry_wait')`),
  index('meta_events_lease_expiry_idx')
    .on(table.leaseExpiresAt)
    .where(sql`${table.status} = 'claimed'`),
  index('meta_events_recovery_owner_idx')
    .on(table.recoveryOwner)
    .where(sql`${table.recoveryOwner} is not null`),
  index('meta_events_customer_erasure_idx')
    .on(table.customerId)
    .where(sql`${table.customerId} is not null`),
  index('meta_events_occurrence_erasure_idx')
    .on(table.sourceOccurrenceId)
    .where(sql`${table.sourceOccurrenceId} is not null`),
  index('meta_events_terminal_health_idx').on(table.status, table.terminalAt),
  check('meta_events_status_ck', sql`${table.status} in ('legacy_non_claimable','queued','claimed','retry_wait','paused','provider_accepted','ambiguous','terminal','suppressed','expired','superseded','voided','recovery_owned','upload_ambiguous')`),
  check('meta_events_claim_contract_ck', sql`not ${table.claimable} or (${table.tenantKey} is not null and ${table.sourceObjectType} is not null and ${table.sourceOccurrenceId} is not null and ${table.sourceObjectVersion} is not null and ${table.contractKey} is not null and ${table.projectionKey} is not null and ${table.destinationKey} is not null and ${table.producerContractVersion} is not null and ${table.minimumDispatcherCapability} is not null and ${table.originalEventAt} is not null and ${table.completionChannelVersion} is not null and ${table.actionSourceMappingVersion} is not null and ${table.configFingerprint} is not null and ${table.payloadDigest} is not null and ${table.subjectIdAtOccurrence} is not null and ${table.suppressionEpochAtOccurrence} is not null and ${table.authorizationIdAtOccurrence} is not null)`),
  check('meta_events_config_fingerprint_ck', sql`${table.configFingerprint} is null or ${table.configFingerprint} ~ '^[a-f0-9]{64}$'`),
]);

// Canonical tracking policy and subject state (U8, add-only). Text vocabularies
// are constrained by reviewed SQL rather than PostgreSQL enums so a future
// version can expand them without rewriting existing rows.
export const trackingPolicyVersions = pgTable(
  'tracking_policy_versions',
  {
    id: text('id').primaryKey(),
    tenantKey: text('tenant_key').notNull(),
    policyVersion: text('policy_version').notNull(),
    status: text('status').notNull(),
    purposeCode: text('purpose_code').notNull(),
    // Explicit reviewed scope; an active policy alone never implies all-source approval.
    allSource: boolean('all_source').notNull().default(false),
    approvalReference: text('approval_reference').notNull(),
    approvalVersion: text('approval_version').notNull(),
    approvedBy: text('approved_by').notNull(),
    privacyNoticeVersion: text('privacy_notice_version').notNull(),
    recipientTopologyVersion: text('recipient_topology_version').notNull(),
    objectionRouteVersion: text('objection_route_version').notNull(),
    effectiveAt: timestamp('effective_at', { withTimezone: true }).notNull(),
    expiresAt: timestamp('expires_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex('tracking_policy_versions_tenant_version_uq').on(
      table.tenantKey,
      table.policyVersion
    ),
  ]
);

export const metaSubjects = pgTable(
  'meta_subjects',
  {
    id: text('id').primaryKey(),
    tenantKey: text('tenant_key').notNull(),
    status: text('status').notNull(),
    currentSuppressionEpoch: integer('current_suppression_epoch').notNull().default(0),
    tombstonedAt: timestamp('tombstoned_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    index('meta_subjects_tenant_status_idx').on(table.tenantKey, table.status),
    check('meta_subjects_nonnegative_epoch_ck', sql`${table.currentSuppressionEpoch} >= 0`),
  ]
);

export const metaSubjectAliases = pgTable(
  'meta_subject_aliases',
  {
    id: text('id').primaryKey(),
    subjectId: text('subject_id').notNull().references(() => metaSubjects.id),
    aliasType: text('alias_type').notNull(),
    aliasHash: text('alias_hash').notNull(),
    normalizationVersion: text('normalization_version').notNull(),
    hashKeyId: text('hash_key_id').notNull(),
    status: text('status').notNull(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    retiredAt: timestamp('retired_at', { withTimezone: true }),
  },
  (table) => [
    uniqueIndex('meta_subject_aliases_type_hash_uq').on(table.aliasType, table.aliasHash),
    index('meta_subject_aliases_subject_idx').on(table.subjectId, table.status),
    check('meta_subject_aliases_hash_ck', sql`${table.aliasHash} ~ '^[a-f0-9]{64}$'`),
  ]
);

export const metaSubjectLineage = pgTable(
  'meta_subject_lineage',
  {
    id: text('id').primaryKey(),
    fromSubjectId: text('from_subject_id').notNull().references(() => metaSubjects.id),
    toSubjectId: text('to_subject_id').notNull().references(() => metaSubjects.id),
    lineageKind: text('lineage_kind').notNull(),
    reasonCode: text('reason_code').notNull(),
    evidenceDigest: text('evidence_digest').notNull(),
    effectiveAt: timestamp('effective_at', { withTimezone: true }).notNull(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex('meta_subject_lineage_identity_uq').on(
      table.fromSubjectId,
      table.toSubjectId,
      table.lineageKind,
      table.effectiveAt
    ),
    check('meta_subject_lineage_distinct_ck', sql`${table.fromSubjectId} <> ${table.toSubjectId}`),
    check('meta_subject_lineage_kind_ck', sql`${table.lineageKind} in ('merge','split')`),
  ]
);

export const metaSubjectSuppressionEpochs = pgTable(
  'meta_subject_suppression_epochs',
  {
    id: text('id').primaryKey(),
    subjectId: text('subject_id').notNull().references(() => metaSubjects.id),
    epoch: integer('epoch').notNull(),
    status: text('status').notNull(),
    reasonCode: text('reason_code').notNull(),
    evidenceDigest: text('evidence_digest').notNull(),
    effectiveAt: timestamp('effective_at', { withTimezone: true }).notNull(),
    expiresAt: timestamp('expires_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex('meta_subject_suppression_epochs_subject_epoch_uq').on(
      table.subjectId,
      table.epoch
    ),
    index('meta_subject_suppression_epochs_current_idx').on(table.subjectId, table.status),
    check('meta_subject_suppression_epochs_nonnegative_ck', sql`${table.epoch} >= 0`),
  ]
);

export const trackingAuthorizations = pgTable(
  'tracking_authorizations',
  {
    id: text('id').primaryKey(),
    tenantKey: text('tenant_key').notNull(),
    subjectId: text('subject_id').notNull().references(() => metaSubjects.id),
    suppressionEpoch: integer('suppression_epoch').notNull(),
    basis: text('basis').notNull(),
    status: text('status').notNull(),
    authorizationReference: text('authorization_reference').notNull(),
    authorizationVersion: text('authorization_version').notNull(),
    purposeCode: text('purpose_code').notNull(),
    policyVersion: text('policy_version').notNull(),
    privacyNoticeVersion: text('privacy_notice_version').notNull(),
    websiteTrackingChoice: text('website_tracking_choice').$type<TrackingWebsiteChoice>(),
    websiteTrackingChoiceVersion: text('website_tracking_choice_version'),
    effectiveAt: timestamp('effective_at', { withTimezone: true }).notNull(),
    expiresAt: timestamp('expires_at', { withTimezone: true }),
    revokedAt: timestamp('revoked_at', { withTimezone: true }),
    evidenceDigest: text('evidence_digest').notNull(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex('tracking_authorizations_reference_uq').on(
      table.subjectId,
      table.authorizationReference,
      table.authorizationVersion
    ),
    index('tracking_authorizations_active_idx').on(table.subjectId, table.status, table.expiresAt),
    check('tracking_authorizations_nonnegative_epoch_ck', sql`${table.suppressionEpoch} >= 0`),
    check('tracking_authorizations_basis_ck', sql`${table.basis} in ('consent','legitimate_interests','other_approved_basis')`),
  ]
);

export const trackingSourceWatermarks = pgTable(
  'tracking_source_watermarks',
  {
    id: text('id').primaryKey(),
    tenantKey: text('tenant_key').notNull(),
    sourceFamily: text('source_family').notNull(),
    watermarkValue: integer('watermark_value').notNull(),
    captureProtocol: text('capture_protocol').notNull(),
    contractVersion: text('contract_version').notNull(),
    checksum: text('checksum').notNull(),
    capturedAt: timestamp('captured_at', { withTimezone: true }).notNull(),
  },
  (table) => [
    uniqueIndex('tracking_source_watermarks_family_contract_uq').on(
      table.tenantKey,
      table.sourceFamily,
      table.contractVersion
    ),
  ]
);

export const trackingSourceClassifications = pgTable(
  'tracking_source_classifications',
  {
    id: text('id').primaryKey(),
    tenantKey: text('tenant_key').notNull(),
    sourceFamily: text('source_family').notNull(),
    sourceObjectId: text('source_object_id').notNull(),
    sourceObjectVersion: text('source_object_version').notNull(),
    classification: text('classification').notNull(),
    watermarkId: text('watermark_id').notNull().references(() => trackingSourceWatermarks.id),
    reasonCode: text('reason_code').notNull(),
    classifiedAt: timestamp('classified_at', { withTimezone: true }).notNull(),
  },
  (table) => [
    uniqueIndex('tracking_source_classifications_object_uq').on(
      table.tenantKey,
      table.sourceFamily,
      table.sourceObjectId
    ),
    index('tracking_source_classifications_gap_idx').on(
      table.tenantKey,
      table.sourceFamily,
      table.classification
    ),
    check('tracking_source_classifications_value_ck', sql`${table.classification} in ('legacy_non_emitting','release_suppressed','new_eligible_candidate')`),
  ]
);

export const stageOccurrences = pgTable(
  'stage_occurrences',
  {
    id: text('id').primaryKey(),
    tenantKey: text('tenant_key').notNull(),
    customerId: integer('customer_id').notNull().references(() => customers.id),
    subjectId: text('subject_id').references(() => metaSubjects.id),
    idempotencyKey: text('idempotency_key').notNull(),
    sourceObjectType: text('source_object_type').notNull(),
    sourceObjectId: text('source_object_id').notNull(),
    sourceObjectVersion: text('source_object_version').notNull(),
    fromStageKey: text('from_stage_key'),
    toStageKey: text('to_stage_key').notNull(),
    previousPipelineVersion: integer('previous_pipeline_version').notNull(),
    pipelineVersion: integer('pipeline_version').notNull(),
    completionChannel: text('completion_channel').$type<TrackingCompletionChannel>().notNull(),
    completionChannelVersion: text('completion_channel_version').notNull(),
    actionSource: text('action_source').$type<TrackingMetaActionSource>().notNull(),
    actionSourceMappingVersion: text('action_source_mapping_version').notNull(),
    classification: text('classification').notNull(),
    evidenceDigest: text('evidence_digest').notNull(),
    occurredAt: timestamp('occurred_at', { withTimezone: true }).notNull(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex('stage_occurrences_idempotency_uq').on(table.tenantKey, table.idempotencyKey),
    index('stage_occurrences_customer_time_idx').on(table.customerId, table.occurredAt),
  ]
);

export const commercialIdentities = pgTable(
  'commercial_identities',
  {
    id: text('id').primaryKey(),
    tenantKey: text('tenant_key').notNull(),
    customerId: integer('customer_id').notNull().references(() => customers.id),
    identityKind: text('identity_kind').notNull(),
    sourceSystem: text('source_system').notNull(),
    sourceObjectId: text('source_object_id').notNull(),
    sourceObjectVersion: text('source_object_version').notNull(),
    grossValueMinor: integer('gross_value_minor'),
    currency: text('currency'),
    effectiveAt: timestamp('effective_at', { withTimezone: true }).notNull(),
    evidenceDigest: text('evidence_digest').notNull(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex('commercial_identities_source_uq').on(
      table.tenantKey,
      table.identityKind,
      table.sourceSystem,
      table.sourceObjectId,
      table.sourceObjectVersion
    ),
    check('commercial_identities_money_pair_ck', sql`${table.grossValueMinor} is null and ${table.currency} is null or ${table.grossValueMinor} > 0 and ${table.currency} ~ '^[A-Z]{3}$'`),
  ]
);

export const paymentIdentities = pgTable(
  'payment_identities',
  {
    id: text('id').primaryKey(),
    tenantKey: text('tenant_key').notNull(),
    commercialIdentityId: text('commercial_identity_id').references(() => commercialIdentities.id),
    customerId: integer('customer_id').notNull().references(() => customers.id),
    provider: text('provider').notNull(),
    providerPaymentId: text('provider_payment_id').notNull(),
    paymentKind: text('payment_kind').notNull(),
    amountMinor: integer('amount_minor').notNull(),
    currency: text('currency').notNull(),
    settledAt: timestamp('settled_at', { withTimezone: true }).notNull(),
    evidenceDigest: text('evidence_digest').notNull(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex('payment_identities_provider_uq').on(
      table.tenantKey,
      table.provider,
      table.providerPaymentId
    ),
    check('payment_identities_nonzero_amount_ck', sql`${table.amountMinor} <> 0`),
    check('payment_identities_currency_ck', sql`${table.currency} ~ '^[A-Z]{3}$'`),
  ]
);

// Booking attempts are reserved before Graph I/O. Original confirmation fields
// are written once, together with the outcome/outbox, and never re-dated on retry.
export const pipelineBookings = pgTable(
  'pipeline_bookings',
  {
    id: text('id').primaryKey(),
    tenantKey: text('tenant_key').notNull(),
    customerId: integer('customer_id').references(() => customers.id, { onDelete: 'set null' }),
    idempotencyKey: text('idempotency_key').notNull(),
    requestDigest: text('request_digest').notNull(),
    graphTransactionId: text('graph_transaction_id').notNull(),
    bookingType: text('booking_type').$type<'design_call' | 'site_visit'>().notNull(),
    staffName: text('staff_name').notNull(),
    staffEmail: text('staff_email').notNull(),
    startsAt: timestamp('starts_at', { withTimezone: true }).notNull(),
    endsAt: timestamp('ends_at', { withTimezone: true }).notNull(),
    completionChannel: text('completion_channel').$type<TrackingCompletionChannel>().notNull(),
    acquisitionSource: text('acquisition_source').$type<TrackingAcquisitionSource>().notNull(),
    status: text('status').notNull().default('reserved'),
    leaseToken: text('lease_token'),
    leaseExpiresAt: timestamp('lease_expires_at', { withTimezone: true }),
    providerEventId: text('provider_event_id'),
    confirmedAt: timestamp('confirmed_at', { withTimezone: true }),
    outcomeId: text('outcome_id'),
    outcomeReasonCode: text('outcome_reason_code'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex('pipeline_bookings_request_uq').on(table.tenantKey, table.idempotencyKey),
    uniqueIndex('pipeline_bookings_graph_transaction_uq').on(table.graphTransactionId),
    uniqueIndex('pipeline_bookings_provider_event_uq').on(table.tenantKey, table.staffEmail, table.providerEventId),
    index('pipeline_bookings_customer_time_idx').on(table.customerId, table.confirmedAt),
    check('pipeline_bookings_status_ck', sql`${table.status} in ('reserved','creating','retryable','confirmed','erased')`),
    check('pipeline_bookings_type_ck', sql`${table.bookingType} in ('design_call','site_visit')`),
    check('pipeline_bookings_confirmation_ck', sql`${table.status} = 'erased' or ((${table.status} = 'confirmed') = (${table.providerEventId} is not null and ${table.confirmedAt} is not null))`),
    check('pipeline_bookings_interval_ck', sql`${table.endsAt} > ${table.startsAt}`),
    check('pipeline_bookings_digest_ck', sql`${table.requestDigest} ~ '^[a-f0-9]{64}$'`),
  ]
);

export const businessOutcomes = pgTable(
  'business_outcomes',
  {
    id: text('id').primaryKey(),
    tenantKey: text('tenant_key').notNull(),
    customerId: integer('customer_id').references(() => customers.id),
    subjectId: text('subject_id').references(() => metaSubjects.id),
    authorizationId: text('authorization_id').references(() => trackingAuthorizations.id),
    outcomeKey: text('outcome_key').$type<TrackingOutcomeKey>().notNull(),
    outcomeContractVersion: text('outcome_contract_version').notNull(),
    sourceObjectType: text('source_object_type').notNull(),
    sourceObjectId: text('source_object_id').notNull(),
    sourceObjectVersion: text('source_object_version').notNull(),
    commercialIdentityId: text('commercial_identity_id').references(() => commercialIdentities.id),
    paymentIdentityId: text('payment_identity_id').references(() => paymentIdentities.id),
    occurredAt: timestamp('occurred_at', { withTimezone: true }).notNull(),
    acquisitionSource: text('acquisition_source').$type<TrackingAcquisitionSource>().notNull(),
    acquisitionSourceVersion: text('acquisition_source_version').notNull(),
    completionChannel: text('completion_channel').$type<TrackingCompletionChannel>().notNull(),
    completionChannelVersion: text('completion_channel_version').notNull(),
    actionSource: text('action_source').$type<TrackingMetaActionSource>().notNull(),
    actionSourceMappingVersion: text('action_source_mapping_version').notNull(),
    policyVersion: text('policy_version').notNull(),
    configFingerprint: text('config_fingerprint'),
    evidenceReference: text('evidence_reference').notNull(),
    evidenceDigest: text('evidence_digest').notNull(),
    suppressionEpochAtOccurrence: integer('suppression_epoch_at_occurrence'),
    eligibilityDecision: text('eligibility_decision')
      .$type<TrackingProjectionDecision['decision']>()
      .notNull(),
    eligibilityReasonCode: text('eligibility_reason_code').notNull(),
    valueMinor: integer('value_minor'),
    currency: text('currency'),
    status: text('status').notNull(),
    testEvent: boolean('test_event').notNull().default(false),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex('business_outcomes_source_fact_uq').on(
      table.tenantKey,
      table.outcomeKey,
      table.sourceObjectType,
      table.sourceObjectId,
      table.sourceObjectVersion
    ),
    index('business_outcomes_due_projection_idx').on(
      table.tenantKey,
      table.status,
      table.occurredAt
    ),
    index('business_outcomes_customer_erasure_idx')
      .on(table.customerId)
      .where(sql`${table.customerId} is not null`),
    check(
      'business_outcomes_money_pair_ck',
      sql`${table.valueMinor} is null and ${table.currency} is null or ${table.valueMinor} is not null and ${table.currency} ~ '^[A-Z]{3}$'`
    ),
    check('business_outcomes_config_fingerprint_ck', sql`${table.configFingerprint} ~ '^[a-f0-9]{64}$'`),
    check('business_outcomes_eligible_config_ck', sql`${table.eligibilityDecision} <> 'eligible' or ${table.configFingerprint} is not null`),
  ]
);

export const businessOutcomeCorrections = pgTable(
  'business_outcome_corrections',
  {
    id: text('id').primaryKey(),
    originalOutcomeId: text('original_outcome_id').notNull().references(() => businessOutcomes.id),
    replacementOutcomeId: text('replacement_outcome_id').references(() => businessOutcomes.id),
    correctionKind: text('correction_kind').notNull(),
    sourceObjectType: text('source_object_type').notNull(),
    sourceObjectId: text('source_object_id').notNull(),
    sourceObjectVersion: text('source_object_version').notNull(),
    amountMinor: integer('amount_minor'),
    currency: text('currency'),
    reasonCode: text('reason_code').notNull(),
    evidenceDigest: text('evidence_digest').notNull(),
    effectiveAt: timestamp('effective_at', { withTimezone: true }).notNull(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex('business_outcome_corrections_source_uq').on(
      table.sourceObjectType,
      table.sourceObjectId,
      table.sourceObjectVersion
    ),
    index('business_outcome_corrections_original_idx').on(table.originalOutcomeId),
  ]
);

export const hmacReplayClaims = pgTable(
  'hmac_replay_claims',
  {
    id: text('id').primaryKey(),
    keyId: text('key_id').notNull(),
    nonce: text('nonce').notNull(),
    requestDigest: text('request_digest').notNull(),
    claimedAt: timestamp('claimed_at', { withTimezone: true }).notNull().defaultNow(),
    expiresAt: timestamp('expires_at', { withTimezone: true }).notNull(),
  },
  (table) => [
    uniqueIndex('hmac_replay_claims_key_nonce_uq').on(table.keyId, table.nonce),
    index('hmac_replay_claims_expiry_idx').on(table.expiresAt),
  ]
);

export const metaEventAttempts = pgTable(
  'meta_event_attempts',
  {
    id: text('id').primaryKey(),
    eventId: text('event_id').notNull().references(() => metaEvents.eventId),
    attemptNumber: integer('attempt_number').notNull(),
    leaseToken: text('lease_token').notNull(),
    status: text('status').notNull(),
    authorizedSuppressionEpoch: integer('authorized_suppression_epoch'),
    authorizationDigest: text('authorization_digest'),
    startedAt: timestamp('started_at', { withTimezone: true }).notNull(),
    sendStartedAt: timestamp('send_started_at', { withTimezone: true }),
    completedAt: timestamp('completed_at', { withTimezone: true }),
    httpStatus: integer('http_status'),
    failureClass: text('failure_class'),
    responseDigest: text('response_digest'),
    sanitizedReceipt: jsonb('sanitized_receipt'),
    dispatcherSha: text('dispatcher_sha').notNull(),
  },
  (table) => [
    uniqueIndex('meta_event_attempts_event_number_uq').on(table.eventId, table.attemptNumber),
    uniqueIndex('meta_event_attempts_event_lease_uq').on(table.eventId, table.leaseToken),
    index('meta_event_attempts_status_time_idx').on(table.status, table.startedAt),
  ]
);

export const recoveryManifests = pgTable(
  'recovery_manifests',
  {
    id: text('id').primaryKey(),
    tenantKey: text('tenant_key').notNull(),
    destinationKey: text('destination_key').notNull(),
    transport: text('transport').notNull(),
    status: text('status').notNull(),
    fencingToken: text('fencing_token').notNull(),
    manifestChecksum: text('manifest_checksum').notNull(),
    rowCount: integer('row_count').notNull(),
    approvedAt: timestamp('approved_at', { withTimezone: true }),
    authorizationExpiresAt: timestamp('authorization_expires_at', { withTimezone: true }).notNull(),
    uploadStartedAt: timestamp('upload_started_at', { withTimezone: true }),
    completedAt: timestamp('completed_at', { withTimezone: true }),
    sanitizedReceipt: jsonb('sanitized_receipt'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex('recovery_manifests_fencing_uq').on(table.fencingToken),
    index('recovery_manifests_due_idx').on(table.status, table.authorizationExpiresAt),
    check('recovery_manifests_nonnegative_rows_ck', sql`${table.rowCount} >= 0`),
  ]
);

export const recoveryManifestRows = pgTable(
  'recovery_manifest_rows',
  {
    id: text('id').primaryKey(),
    manifestId: text('manifest_id').notNull().references(() => recoveryManifests.id),
    eventId: text('event_id').notNull().references(() => metaEvents.eventId),
    rowChecksum: text('row_checksum').notNull(),
    authorizationDigest: text('authorization_digest').notNull(),
    status: text('status').notNull(),
    providerRowReference: text('provider_row_reference'),
    sanitizedResult: jsonb('sanitized_result'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex('recovery_manifest_rows_manifest_event_uq').on(table.manifestId, table.eventId),
    index('recovery_manifest_rows_status_idx').on(table.manifestId, table.status),
  ]
);

export const trackingProviderReceipts = pgTable(
  'tracking_provider_receipts',
  {
    id: text('id').primaryKey(),
    eventId: text('event_id').notNull().references(() => metaEvents.eventId),
    attemptId: text('attempt_id').references(() => metaEventAttempts.id),
    receiptKind: text('receipt_kind').notNull(),
    providerStatus: text('provider_status').notNull(),
    providerEventReference: text('provider_event_reference'),
    countedEvidenceReference: text('counted_evidence_reference'),
    deduplicationEvidenceReference: text('deduplication_evidence_reference'),
    sanitizedPayload: jsonb('sanitized_payload'),
    observedAt: timestamp('observed_at', { withTimezone: true }).notNull(),
  },
  (table) => [
    uniqueIndex('tracking_provider_receipts_kind_uq').on(table.eventId, table.receiptKind),
    index('tracking_provider_receipts_observed_idx').on(table.receiptKind, table.observedAt),
  ]
);

export const sideEffectJobs = pgTable(
  'side_effect_jobs',
  {
    id: text('id').primaryKey(),
    idempotencyKey: text('idempotency_key').notNull(),
    jobKind: text('job_kind').notNull(),
    sourceReceiptId: text('source_receipt_id'),
    payloadDigest: text('payload_digest').notNull(),
    encryptedPayload: text('encrypted_payload').notNull(),
    encryptionKeyId: text('encryption_key_id').notNull(),
    status: text('status').notNull(),
    attemptCount: integer('attempt_count').notNull().default(0),
    nextAttemptAt: timestamp('next_attempt_at', { withTimezone: true }),
    leaseToken: text('lease_token'),
    leaseOwner: text('lease_owner'),
    leaseExpiresAt: timestamp('lease_expires_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex('side_effect_jobs_idempotency_uq').on(table.idempotencyKey),
    index('side_effect_jobs_due_idx').on(table.status, table.nextAttemptAt),
    index('side_effect_jobs_lease_idx').on(table.leaseExpiresAt),
  ]
);

export const sideEffectJobAttempts = pgTable(
  'side_effect_job_attempts',
  {
    id: text('id').primaryKey(),
    jobId: text('job_id').notNull().references(() => sideEffectJobs.id),
    attemptNumber: integer('attempt_number').notNull(),
    leaseToken: text('lease_token').notNull(),
    status: text('status').notNull(),
    providerIdempotencyKey: text('provider_idempotency_key'),
    failureClass: text('failure_class'),
    startedAt: timestamp('started_at', { withTimezone: true }).notNull(),
    completedAt: timestamp('completed_at', { withTimezone: true }),
  },
  (table) => [uniqueIndex('side_effect_job_attempts_number_uq').on(table.jobId, table.attemptNumber)]
);

export const sideEffectJobReceipts = pgTable(
  'side_effect_job_receipts',
  {
    id: text('id').primaryKey(),
    jobId: text('job_id').notNull().references(() => sideEffectJobs.id),
    attemptId: text('attempt_id').references(() => sideEffectJobAttempts.id),
    invocationId: text('invocation_id').notNull(),
    schedulerSha: text('scheduler_sha').notNull(),
    serviceSha: text('service_sha').notNull(),
    authKeyId: text('auth_key_id').notNull(),
    authNonce: text('auth_nonce').notNull(),
    lockResult: text('lock_result').notNull(),
    status: text('status').notNull(),
    providerState: text('provider_state'),
    sanitizedReceipt: jsonb('sanitized_receipt').notNull(),
    startedAt: timestamp('started_at', { withTimezone: true }).notNull(),
    finishedAt: timestamp('finished_at', { withTimezone: true }),
  },
  (table) => [
    uniqueIndex('side_effect_job_receipts_invocation_uq').on(table.invocationId),
    index('side_effect_job_receipts_health_idx').on(table.status, table.finishedAt),
  ]
);

/**
 * Immutable receipt of one genuine landing-page submission occurrence.
 *
 * The occurrence identity and non-identifying audit fields survive customer
 * erasure. Raw/normalised PII may only be cleared by the reviewed erasure
 * service, which records `piiErasedAt`; operational state belongs in the work
 * item and activity tables below rather than being written back here.
 */
export const leadEnquiries = pgTable(
  'lead_enquiries',
  {
    id: text('id').primaryKey(),
    source: text('source').notNull(),
    submissionEventId: text('submission_event_id').notNull(),
    customerId: integer('customer_id').references(() => customers.id, { onDelete: 'restrict' }),
    candidateCustomerIds: jsonb('candidate_customer_ids').$type<number[]>(),
    classification: text('classification').notNull(),
    identityReviewStatus: text('identity_review_status').notNull().default('not_required'),
    identityReviewReason: text('identity_review_reason'),
    landingQuoteId: integer('landing_quote_id'),
    landingReference: text('landing_reference'),
    receivedAt: timestamp('received_at', { withTimezone: true }).notNull().defaultNow(),
    originalEventAt: timestamp('original_event_at', { withTimezone: true }).notNull(),
    submittedFirstName: text('submitted_first_name'),
    submittedLastName: text('submitted_last_name'),
    submittedEmail: text('submitted_email'),
    submittedPhone: text('submitted_phone'),
    submittedPostcode: text('submitted_postcode'),
    submittedAddressLine1: text('submitted_address_line_1'),
    submittedAddressLine2: text('submitted_address_line_2'),
    normalizedName: text('normalized_name'),
    normalizedEmail: text('normalized_email'),
    normalizedPhone: text('normalized_phone'),
    normalizedPostcode: text('normalized_postcode'),
    normalizedAddress: text('normalized_address'),
    propertyEvidence: jsonb('property_evidence'),
    consentSnapshot: jsonb('consent_snapshot'),
    trackingSnapshot: jsonb('tracking_snapshot'),
    formSnapshot: jsonb('form_snapshot'),
    historicalRescueRunId: text('historical_rescue_run_id'),
    piiErasedAt: timestamp('pii_erased_at', { withTimezone: true }),
    landingErasureStatus: text('landing_erasure_status').notNull().default('not_required'),
    landingErasureRequestId: text('landing_erasure_request_id'),
    landingErasureRequestedAt: timestamp('landing_erasure_requested_at', { withTimezone: true }),
    landingErasureCompletedAt: timestamp('landing_erasure_completed_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex('lead_enquiries_source_submission_uq').on(table.source, table.submissionEventId),
    index('lead_enquiries_customer_received_idx').on(table.customerId, table.receivedAt),
    index('lead_enquiries_review_idx').on(table.identityReviewStatus, table.createdAt),
    check('lead_enquiries_classification_ck', sql`${table.classification} in ('new_customer', 'repeat_enquiry', 'identity_conflict')`),
    check('lead_enquiries_review_status_ck', sql`${table.identityReviewStatus} in ('not_required', 'pending', 'lifecycle_review')`),
    check('lead_enquiries_erasure_ck', sql`(${table.piiErasedAt} is null and ${table.customerId} is not null) or (${table.piiErasedAt} is not null and ${table.customerId} is null and ${table.candidateCustomerIds} is null and ${table.submittedFirstName} is null and ${table.submittedLastName} is null and ${table.submittedEmail} is null and ${table.submittedPhone} is null and ${table.submittedPostcode} is null and ${table.submittedAddressLine1} is null and ${table.submittedAddressLine2} is null and ${table.normalizedName} is null and ${table.normalizedEmail} is null and ${table.normalizedPhone} is null and ${table.normalizedPostcode} is null and ${table.normalizedAddress} is null and ${table.propertyEvidence} is null and ${table.consentSnapshot} is null and ${table.trackingSnapshot} is null and ${table.formSnapshot} is null)`),
    check('lead_enquiries_landing_erasure_ck', sql`(${table.landingErasureStatus} = 'not_required' and ${table.landingErasureRequestId} is null and ${table.landingErasureRequestedAt} is null and ${table.landingErasureCompletedAt} is null) or (${table.landingErasureStatus} = 'pending' and ${table.landingQuoteId} is not null and ${table.piiErasedAt} is not null and ${table.landingErasureRequestId} is not null and ${table.landingErasureRequestedAt} is not null and ${table.landingErasureCompletedAt} is null) or (${table.landingErasureStatus} = 'completed' and ${table.landingQuoteId} is not null and ${table.piiErasedAt} is not null and ${table.landingErasureRequestId} is not null and ${table.landingErasureRequestedAt} is not null and ${table.landingErasureCompletedAt} is not null)`),
    index('lead_enquiries_landing_erasure_pending_idx').on(table.landingErasureRequestedAt, table.id).where(sql`${table.landingErasureStatus} = 'pending'`),
  ]
);

export const leadEnquiryWorkItems = pgTable(
  'lead_enquiry_work_items',
  {
    id: text('id').primaryKey(),
    enquiryId: text('enquiry_id').notNull().references(() => leadEnquiries.id, { onDelete: 'restrict' }),
    assignedTo: integer('assigned_to').references(() => users.id, { onDelete: 'set null' }),
    assignedAt: timestamp('assigned_at', { withTimezone: true }),
    assignedBy: integer('assigned_by').references(() => users.id, { onDelete: 'set null' }),
    firstContactedAt: timestamp('first_contacted_at', { withTimezone: true }),
    firstContactedBy: integer('first_contacted_by').references(() => users.id, { onDelete: 'set null' }),
    closureState: text('closure_state'),
    closedAt: timestamp('closed_at', { withTimezone: true }),
    identityReviewRequired: boolean('identity_review_required').notNull().default(false),
    waitingAt: timestamp('waiting_at', { withTimezone: true }).notNull(),
    needsAttentionAt: timestamp('needs_attention_at', { withTimezone: true }).notNull(),
    version: integer('version').notNull().default(0),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex('lead_enquiry_work_items_enquiry_uq').on(table.enquiryId),
    index('lead_enquiry_work_items_queue_idx').on(table.needsAttentionAt, table.waitingAt, table.createdAt).where(sql`${table.closedAt} is null and ${table.firstContactedAt} is null and ${table.assignedTo} is null`),
    index('lead_enquiry_work_items_assignee_idx').on(table.assignedTo, table.createdAt).where(sql`${table.closedAt} is null and ${table.firstContactedAt} is null and ${table.assignedTo} is not null`),
    check('lead_enquiry_work_items_version_ck', sql`${table.version} >= 0`),
  ]
);

export const leadEnquiryActivities = pgTable(
  'lead_enquiry_activities',
  {
    id: text('id').primaryKey(),
    enquiryId: text('enquiry_id').notNull().references(() => leadEnquiries.id, { onDelete: 'restrict' }),
    actorUserId: integer('actor_user_id').references(() => users.id, { onDelete: 'set null' }),
    activityType: text('activity_type').notNull(),
    idempotencyKey: text('idempotency_key'),
    metadata: jsonb('metadata'),
    occurredAt: timestamp('occurred_at', { withTimezone: true }).notNull(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex('lead_enquiry_activities_idempotency_uq').on(table.enquiryId, table.idempotencyKey).where(sql`${table.idempotencyKey} is not null`),
    index('lead_enquiry_activities_enquiry_time_idx').on(table.enquiryId, table.occurredAt, table.id),
    check('lead_enquiry_activities_type_ck', sql`${table.activityType} in ('claimed', 'assigned', 'unassigned', 'contact_attempt', 'no_answer', 'contacted', 'identity_reviewed', 'reactivated', 'rescued', 'closed', 'pii_erased')`),
  ]
);

export const leadDigestRuns = pgTable(
  'lead_digest_runs',
  {
    id: text('id').primaryKey(),
    businessDate: date('business_date').notNull(),
    digestKind: text('digest_kind').notNull(),
    snapshotCount: integer('snapshot_count').notNull(),
    snapshotHash: text('snapshot_hash').notNull(),
    status: text('status').notNull().default('started'),
    startedAt: timestamp('started_at', { withTimezone: true }).notNull().defaultNow(),
    completedAt: timestamp('completed_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex('lead_digest_runs_date_kind_uq').on(table.businessDate, table.digestKind),
    index('lead_digest_runs_status_idx').on(table.status, table.startedAt),
    check('lead_digest_runs_count_ck', sql`${table.snapshotCount} >= 0`),
    check('lead_digest_runs_status_ck', sql`${table.status} in ('started', 'completed', 'failed')`),
  ]
);

export const leadEmailIntents = pgTable(
  'lead_email_intents',
  {
    id: text('id').primaryKey(),
    businessKey: text('business_key').notNull(),
    enquiryId: text('enquiry_id').references(() => leadEnquiries.id, { onDelete: 'restrict' }),
    digestRunId: text('digest_run_id').references(() => leadDigestRuns.id, { onDelete: 'restrict' }),
    audience: text('audience').notNull(),
    templateVersion: text('template_version').notNull(),
    recipientEmail: text('recipient_email'),
    recipientHash: text('recipient_hash').notNull(),
    emailOwner: text('email_owner').notNull().default('crm'),
    ownerGeneration: text('owner_generation').notNull(),
    state: text('state').notNull().default('pending'),
    provider: text('provider').notNull().default('postmark'),
    providerMessageId: text('provider_message_id'),
    attemptCount: integer('attempt_count').notNull().default(0),
    nextAttemptAt: timestamp('next_attempt_at', { withTimezone: true }).notNull().defaultNow(),
    leaseOwner: text('lease_owner'),
    leaseToken: text('lease_token'),
    leaseExpiresAt: timestamp('lease_expires_at', { withTimezone: true }),
    acceptedAt: timestamp('accepted_at', { withTimezone: true }),
    deliveredAt: timestamp('delivered_at', { withTimezone: true }),
    bouncedAt: timestamp('bounced_at', { withTimezone: true }),
    acceptanceUnknownAt: timestamp('acceptance_unknown_at', { withTimezone: true }),
    suppressedAt: timestamp('suppressed_at', { withTimezone: true }),
    sanitizedResponse: jsonb('sanitized_response'),
    sanitizedError: jsonb('sanitized_error'),
    piiErasedAt: timestamp('pii_erased_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex('lead_email_intents_business_key_uq').on(table.businessKey),
    index('lead_email_intents_provider_message_idx').on(table.providerMessageId).where(sql`${table.providerMessageId} is not null`),
    index('lead_email_intents_due_idx').on(table.nextAttemptAt, table.createdAt).where(sql`${table.state} in ('pending', 'acceptance_unknown')`),
    index('lead_email_intents_lease_expiry_idx').on(table.leaseExpiresAt).where(sql`${table.state} = 'leased'`),
    index('lead_email_intents_enquiry_idx').on(table.enquiryId, table.createdAt),
    check('lead_email_intents_source_ck', sql`(${table.enquiryId} is not null and ${table.digestRunId} is null) or (${table.enquiryId} is null and ${table.digestRunId} is not null)`),
    check('lead_email_intents_owner_ck', sql`${table.emailOwner} in ('landing', 'crm')`),
    check('lead_email_intents_state_ck', sql`${table.state} in ('pending', 'leased', 'accepted', 'acceptance_unknown', 'delivered', 'bounced', 'suppressed', 'dead')`),
    check('lead_email_intents_attempt_count_ck', sql`${table.attemptCount} >= 0`),
    check('lead_email_intents_erasure_ck', sql`${table.piiErasedAt} is null or ${table.recipientEmail} is null`),
  ]
);

export const leadEmailAttempts = pgTable(
  'lead_email_attempts',
  {
    id: text('id').primaryKey(),
    intentId: text('intent_id').notNull().references(() => leadEmailIntents.id, { onDelete: 'restrict' }),
    attemptNumber: integer('attempt_number').notNull(),
    leaseToken: text('lease_token').notNull(),
    status: text('status').notNull(),
    providerMessageId: text('provider_message_id'),
    startedAt: timestamp('started_at', { withTimezone: true }).notNull(),
    completedAt: timestamp('completed_at', { withTimezone: true }),
    httpStatus: integer('http_status'),
    failureClass: text('failure_class'),
    responseDigest: text('response_digest'),
    sanitizedReceipt: jsonb('sanitized_receipt'),
  },
  (table) => [
    uniqueIndex('lead_email_attempts_intent_number_uq').on(table.intentId, table.attemptNumber),
    uniqueIndex('lead_email_attempts_intent_lease_uq').on(table.intentId, table.leaseToken),
    index('lead_email_attempts_status_time_idx').on(table.status, table.startedAt),
    check('lead_email_attempts_number_ck', sql`${table.attemptNumber} > 0`),
  ]
);

export const postmarkWebhookReceipts = pgTable(
  'postmark_webhook_receipts',
  {
    id: text('id').primaryKey(),
    receiptKey: text('receipt_key').notNull(),
    intentId: text('intent_id').references(() => leadEmailIntents.id, { onDelete: 'restrict' }),
    eventType: text('event_type').notNull(),
    messageId: text('message_id').notNull(),
    recipientHash: text('recipient_hash').notNull(),
    providerEventAt: timestamp('provider_event_at', { withTimezone: true }).notNull(),
    receivedAt: timestamp('received_at', { withTimezone: true }).notNull().defaultNow(),
    minimizedPayload: jsonb('minimized_payload').notNull(),
    appliedAt: timestamp('applied_at', { withTimezone: true }),
  },
  (table) => [
    uniqueIndex('postmark_webhook_receipts_key_uq').on(table.receiptKey),
    index('postmark_webhook_receipts_message_idx').on(table.messageId, table.providerEventAt),
    index('postmark_webhook_receipts_unapplied_idx').on(table.receivedAt).where(sql`${table.appliedAt} is null`),
  ]
);

// Customer metadata for Meta tracking with enhanced fields
export const customerMetadata = pgTable('customer_metadata', {
  id: serial('id').primaryKey(),
  customerId: integer('customer_id').references(() => customers.id).unique().notNull(),
  leadId: text('lead_id').unique(),
  fbp: text('fbp'), // Facebook Browser ID
  fbc: text('fbc'), // Facebook Click ID
  fbclid: text('fbclid'), // Facebook Click ID from URL
  gclid: text('gclid'), // Google Click ID from URL
  sessionId: text('session_id'), // Session tracking
  firstTouchpoint: text('first_touchpoint'),
  lastTouchpoint: text('last_touchpoint'),
  utmSource: text('utm_source'),
  utmMedium: text('utm_medium'),
  utmCampaign: text('utm_campaign'),
  utmTerm: text('utm_term'),
  utmContent: text('utm_content'),
  landingPageUrl: text('landing_page_url'),
  dataQualityScore: integer('data_quality_score'), // 0-100 score
  matchQualityScore: decimal('match_quality_score', { precision: 4, scale: 2 }), // Meta's match quality score
  clickIds: jsonb('click_ids'), // Store all click IDs as JSON
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// Equipment options table for custom equipment in site surveys
export const equipmentOptions = pgTable('equipment_options', {
  id: serial('id').primaryKey(),
  type: text('type').notNull(), // 'panel', 'inverter', 'battery'
  category: text('category'), // For inverters: 'sigenergySinglePhase', 'sigenergyThreePhase', 'hanchuSinglePhase', etc.
  value: text('value').notNull().unique(), // The actual value stored
  label: text('label').notNull(), // Display label
  description: text('description'), // Optional description
  specifications: jsonb('specifications'), // Optional specs like power rating, etc.
  createdAt: timestamp('created_at').defaultNow().notNull(),
  createdBy: integer('created_by').references(() => users.id),
  isActive: boolean('is_active').default(true).notNull(),
  isCustom: boolean('is_custom').default(true).notNull(), // To distinguish from hardcoded options
});

// ============================================================================
// SCHEDULING SYSTEM TABLES
// ============================================================================

// Staff Profiles - Extended staff information
export const staffProfiles = pgTable('staff_profiles', {
  id: serial('id').primaryKey(),
  userId: integer('user_id').references(() => users.id).notNull(),
  email: text('email'), // Staff email address
  employmentType: text('employment_type').notNull(), // 'full_time' | 'part_time' | 'contractor'
  jobRole: text('job_role').notNull(), // 'lead_installer' | 'electrician_mate' | 'electrician_labourer' | 'lead_roofer' | 'roofer_2' | 'labourer'
  hourlyRate: numeric('hourly_rate', { precision: 10, scale: 2 }),
  skills: jsonb('skills').$type<string[]>().default([]), // ['solar_installation', 'electrical', 'roofing']
  certifications: jsonb('certifications').$type<Array<{
    name: string;
    issuer: string;
    expiryDate?: string;
  }>>().default([]),
  maxJobsPerDay: integer('max_jobs_per_day').default(1),
  requiresSupervision: boolean('requires_supervision').default(false),
  isActive: boolean('is_active').default(true).notNull(),
  phoneNumber: text('phone_number'),
  emailNotifications: boolean('email_notifications').default(true).notNull(),
  smsNotifications: boolean('sms_notifications').default(false).notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// Staff Availability - Weekly availability patterns
export const staffAvailability = pgTable('staff_availability', {
  id: serial('id').primaryKey(),
  staffId: integer('staff_id').references(() => staffProfiles.id).notNull(),
  dayOfWeek: integer('day_of_week').notNull(), // 0=Sunday, 6=Saturday
  startTime: text('start_time').notNull(), // HH:MM format
  endTime: text('end_time').notNull(), // HH:MM format
  isAvailable: boolean('is_available').default(true).notNull(),
  effectiveFrom: date('effective_from'),
  effectiveUntil: date('effective_until'), // null = ongoing
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// Monthly Availability Submissions - Track when crew submit availability
export const monthlyAvailability = pgTable('monthly_availability', {
  id: serial('id').primaryKey(),
  staffId: integer('staff_id').references(() => staffProfiles.id).notNull(),
  month: integer('month').notNull(), // 1-12
  year: integer('year').notNull(), // 2025
  submittedAt: timestamp('submitted_at').defaultNow().notNull(),
  submittedVia: text('submitted_via').default('portal'), // 'portal' | 'email' | 'admin'
  notes: text('notes'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

// Crew Access Tokens - Token-based authentication for crew portal
export const crewAccessTokens = pgTable('crew_access_tokens', {
  id: serial('id').primaryKey(),
  staffId: integer('staff_id').references(() => staffProfiles.id).notNull(),
  token: text('token').unique().notNull(),
  expiresAt: timestamp('expires_at').notNull(),
  lastUsedAt: timestamp('last_used_at'),
  isRevoked: boolean('is_revoked').default(false).notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

// Staff Leave - Annual leave tracking (full-time staff only, admin managed)
export const staffLeave = pgTable('staff_leave', {
  id: serial('id').primaryKey(),
  staffId: integer('staff_id').references(() => staffProfiles.id).notNull(),
  leaveType: text('leave_type').notNull(), // 'annual', 'sick', 'unpaid', 'other'
  startDate: date('start_date').notNull(),
  endDate: date('end_date').notNull(),
  notes: text('notes'),
  addedBy: integer('added_by').references(() => users.id).notNull(), // Admin who added the leave
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// Job Schedules - Main scheduling table (linked to Won stage in pipeline)
export const jobSchedules = pgTable('job_schedules', {
  id: serial('id').primaryKey(),
  customerId: integer('customer_id').references(() => customers.id).notNull(), // Link to Won customer in pipeline
  installationId: integer('installation_id').references(() => installations.id), // Optional: Link to installation if created
  scheduledDate: date('scheduled_date').notNull(),
  scheduledStartTime: text('scheduled_start_time').notNull(), // HH:MM format
  scheduledEndTime: text('scheduled_end_time').notNull(), // HH:MM format
  estimatedDurationHours: numeric('estimated_duration_hours', { precision: 5, scale: 2 }),
  actualStartTime: timestamp('actual_start_time'),
  actualEndTime: timestamp('actual_end_time'),
  status: text('status').default('scheduled').notNull(), // 'scheduled', 'in_progress', 'completed', 'cancelled', 'rescheduled'
  crewSizeRequired: integer('crew_size_required').default(2).notNull(),
  rolesRequired: jsonb('roles_required').$type<Array<{
    role: string;
    count: number;
  }>>().default([]),
  priority: text('priority').default('medium'), // 'low', 'medium', 'high', 'urgent'
  notes: text('notes'),
  cancellationReason: text('cancellation_reason'),
  rescheduledFrom: integer('rescheduled_from').references(() => jobSchedules.id),
  createdBy: integer('created_by').references(() => users.id),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// Job Assignments - Crew assignments to jobs
export const jobAssignments = pgTable('job_assignments', {
  id: serial('id').primaryKey(),
  jobScheduleId: integer('job_schedule_id').references(() => jobSchedules.id).notNull(),
  staffId: integer('staff_id').references(() => staffProfiles.id).notNull(),
  roleAssigned: text('role_assigned').notNull(), // 'lead_installer', 'electrician_mate', etc.
  assignedAt: timestamp('assigned_at').defaultNow().notNull(),
  assignedBy: integer('assigned_by').references(() => users.id),
  status: text('status').default('assigned').notNull(), // 'assigned', 'confirmed', 'completed', 'no_show'
  notes: text('notes'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// Scheduling Rules - Configurable business rules
export const schedulingRules = pgTable('scheduling_rules', {
  id: serial('id').primaryKey(),
  ruleName: text('rule_name').notNull(),
  ruleType: text('rule_type').notNull(), // 'crew_composition', 'role_requirement', 'time_constraint'
  ruleConfig: jsonb('rule_config').notNull().$type<{
    minCrewSize?: number;
    maxCrewSize?: number;
    requiredRoles?: Array<{ role: string; min: number; max?: number }>;
    [key: string]: any;
  }>(),
  appliesTo: text('applies_to').default('all'), // 'all', 'residential', 'commercial'
  isActive: boolean('is_active').default(true).notNull(),
  priority: integer('priority').default(0).notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// Weather Notes - Manual weather tracking with optional API fetch
export const weatherNotes = pgTable('weather_notes', {
  id: serial('id').primaryKey(),
  scheduleDate: date('schedule_date').notNull(),
  location: text('location').notNull(), // Postcode or area
  weatherCondition: text('weather_condition').notNull(), // 'clear', 'rain', 'cloudy', 'snow', 'storm', 'windy'
  temperatureCelsius: numeric('temperature_celsius', { precision: 5, scale: 2 }),
  windSpeedMph: numeric('wind_speed_mph', { precision: 5, scale: 2 }),
  notes: text('notes'),
  isSuitableForInstallation: boolean('is_suitable_for_installation').default(true).notNull(),
  impactReason: text('impact_reason'),
  enteredBy: integer('entered_by').references(() => users.id).notNull(),
  autoFetched: boolean('auto_fetched').default(false).notNull(),
  source: text('source').default('manual').notNull(), // 'manual' or API name
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// Schedule Conflicts - Detected conflicts log
export const scheduleConflicts = pgTable('schedule_conflicts', {
  id: serial('id').primaryKey(),
  jobScheduleId: integer('job_schedule_id').references(() => jobSchedules.id).notNull(),
  conflictType: text('conflict_type').notNull(), // 'staff_unavailable', 'weather', 'double_booking', 'role_gap'
  description: text('description').notNull(),
  severity: text('severity').default('warning').notNull(), // 'warning', 'error', 'critical'
  resolutionStatus: text('resolution_status').default('unresolved').notNull(), // 'unresolved', 'resolved', 'ignored'
  resolvedAt: timestamp('resolved_at'),
  resolvedBy: integer('resolved_by').references(() => users.id),
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

// Bill of Materials (BOM) Items - Tracks materials for each site survey
export const bomItems = pgTable('bom_items', {
  id: serial('id').primaryKey(),
  surveyId: integer('survey_id').references(() => siteSurveys.id, { onDelete: 'cascade' }).notNull(),
  category: text('category').notNull(), // 'inverter', 'battery', 'panel', 'evCharger', 'gateway', 'birdguard', 'isolator', 'distribution', 'cable', 'roofAccessories', 'scaffolding', 'other'
  itemName: text('item_name').notNull(),
  description: text('description'),
  quantity: integer('quantity').default(1).notNull(),
  unit: text('unit').default('pcs').notNull(), // 'pcs', 'meters', 'sets', 'rolls'
  supplierNotes: text('supplier_notes'), // Where to buy, supplier info
  notes: text('notes'), // General notes
  isAutoPopulated: boolean('is_auto_populated').default(false).notNull(), // true if from survey, false if manually added
  sourceField: text('source_field'), // tracks which survey field it came from (e.g., 'panelsDescription', 'inverter1')
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
  // Soft-delete state (integration tokens plan U1). Null means the row is live.
  deletedAt: timestamp('deleted_at'),
  deletedByTokenId: integer('deleted_by_token_id'), // integration_tokens.id when deleted by a token caller
});

// BOM Templates - Predefined BOM templates for common system configurations
export const bomTemplates = pgTable('bom_templates', {
  id: serial('id').primaryKey(),
  name: text('name').notNull(), // e.g., 'Single Phase 5kW System', '3-Phase 10kW System'
  description: text('description'),
  systemType: text('system_type'), // 'single-phase', 'three-phase'
  systemSize: text('system_size'), // e.g., '5kW', '10kW', '15kW'
  templateItems: jsonb('template_items').$type<Array<{
    category: string;
    itemName: string;
    description?: string;
    quantity: number;
    unit: string;
    notes?: string;
  }>>().notNull(), // Array of default items for this template
  createdBy: integer('created_by').references(() => users.id),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
  isActive: boolean('is_active').default(true).notNull(),
});

// BOM Suppliers - Reusable supplier names with usage tracking
export const bomSuppliers = pgTable('bom_suppliers', {
  id: serial('id').primaryKey(),
  name: text('name').notNull().unique(),
  description: text('description'),
  website: text('website'),
  contactInfo: text('contact_info'),
  usageCount: integer('usage_count').default(1).notNull(),
  lastUsed: timestamp('last_used').defaultNow().notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  createdBy: integer('created_by').references(() => users.id),
  isActive: boolean('is_active').default(true).notNull(),
});

// BOM Custom Items - User-created reusable items for quick addition to surveys
export const bomCustomItems = pgTable('bom_custom_items', {
  id: serial('id').primaryKey(),
  itemName: text('item_name').notNull(),
  category: text('category').notNull(), // Same categories as bomItems
  unit: text('unit').default('pcs').notNull(), // 'pcs', 'meters', 'sets', 'rolls', 'boxes'
  notes: text('notes'), // Default notes for this custom item
  usageCount: integer('usage_count').default(0).notNull(), // Track how often it's used
  lastUsed: timestamp('last_used'),
  createdBy: integer('created_by').references(() => users.id),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
  isActive: boolean('is_active').default(true).notNull(),
});

// ============================================================================
// Support Ticket System
// ============================================================================

// Equipment providers (for escalation tracking)
export const equipmentProviders = pgTable('equipment_providers', {
  id: serial('id').primaryKey(),
  name: text('name').notNull().unique(),
  contactPhone: text('contact_phone'),
  contactEmail: text('contact_email'),
  supportUrl: text('support_url'),
  notes: text('notes'),
  isActive: boolean('is_active').default(true),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// Support tickets table
export const supportTickets = pgTable('support_tickets', {
  id: serial('id').primaryKey(),
  ticketNumber: text('ticket_number').notNull().unique(), // T001, T002, etc.

  // Customer link (optional - can be null for standalone tickets)
  customerId: integer('customer_id').references(() => customers.id),
  installationId: integer('installation_id').references(() => installations.id),

  // Standalone customer info (when not linked to existing customer)
  customerName: text('customer_name'),
  customerPhone: text('customer_phone'),
  customerEmail: text('customer_email'),
  customerAddress: text('customer_address'),

  // Ticket details
  systemProvider: text('system_provider'), // Equipment provider name (Hanchu, SIG, etc.)
  providerId: integer('provider_id').references(() => equipmentProviders.id),
  issueDescription: text('issue_description').notNull(),
  issueCategory: text('issue_category'), // system_offline, performance, hardware, billing, other

  // Status tracking
  status: text('status').default('open'), // open, in_progress, escalated, resolved, closed
  priority: text('priority').default('normal'), // low, normal, high, urgent

  // Provider escalation
  providerTicketRef: text('provider_ticket_ref'), // External ticket reference from provider
  escalatedAt: timestamp('escalated_at'),

  // Resolution
  resolution: text('resolution'),
  resolvedAt: timestamp('resolved_at'),
  resolvedBy: integer('resolved_by').references(() => users.id),

  // Metadata
  createdBy: integer('created_by').references(() => users.id),
  assignedTo: integer('assigned_to').references(() => users.id),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),

  // Email automation fields (for Microsoft Graph integration)
  sourceEmail: text('source_email'),                          // Email address that created this ticket
  sourceEmailMessageId: text('source_email_message_id'),      // Graph message ID for threading
  lastCustomerEmailAt: timestamp('last_customer_email_at'),   // Last email from customer
  originalSubject: text('original_subject'),                  // Original email subject
  smartSubject: text('smart_subject'),                        // AI-generated concise subject (UK spelling)
});

// Timeline/activity log for each ticket
export const supportTicketActivities = pgTable('support_ticket_activities', {
  id: serial('id').primaryKey(),
  ticketId: integer('ticket_id').references(() => supportTickets.id).notNull(),

  activityType: text('activity_type').notNull(), // comment, status_change, escalation, call, email
  content: text('content').notNull(),
  activityTime: timestamp('activity_time'), // Time the activity occurred (can differ from createdAt)

  // For AI analysis - mark steps that led to resolution
  isResolutionStep: boolean('is_resolution_step').default(false),

  createdBy: integer('created_by').references(() => users.id),
  createdAt: timestamp('created_at').defaultNow(),
});

// Images attached to support tickets
export const supportTicketImages = pgTable('support_ticket_images', {
  id: serial('id').primaryKey(),
  ticketId: integer('ticket_id').references(() => supportTickets.id).notNull(),

  filename: text('filename').notNull(),
  originalFilename: text('original_filename').notNull(),
  objectKey: text('object_key').notNull(),
  mimeType: text('mime_type').notNull(),
  size: integer('size').notNull(),

  uploadedBy: integer('uploaded_by').references(() => users.id),
  createdAt: timestamp('created_at').defaultNow(),
});

// Support ticket tags (reusable labels like Google Keep)
export const supportTicketTags = pgTable('support_ticket_tags', {
  id: serial('id').primaryKey(),
  name: text('name').notNull().unique(),
  color: text('color').default('#6b7280'),
  createdBy: integer('created_by').references(() => users.id),
  createdAt: timestamp('created_at').defaultNow(),
});

// Junction table: many-to-many between tickets and tags
export const supportTicketTagAssignments = pgTable('support_ticket_tag_assignments', {
  id: serial('id').primaryKey(),
  ticketId: integer('ticket_id').references(() => supportTickets.id).notNull(),
  tagId: integer('tag_id').references(() => supportTicketTags.id).notNull(),
  assignedBy: integer('assigned_by').references(() => users.id),
  assignedAt: timestamp('assigned_at').defaultNow(),
});

// Inbound email logs for support ticket automation
export const inboundEmailLogs = pgTable('inbound_email_logs', {
  id: serial('id').primaryKey(),
  receivedAt: timestamp('received_at').defaultNow(),
  provider: text('provider').notNull(), // 'graph', 'postmark', 'imap'

  // Sender info
  senderEmail: text('sender_email').notNull(),
  senderName: text('sender_name'),

  // Email content
  subject: text('subject'),
  messageId: text('message_id'),           // Graph message ID
  conversationId: text('conversation_id'), // Graph conversation ID for threading
  rawPayload: jsonb('raw_payload'),        // Full API response for debugging

  // Processing status
  processingStatus: text('processing_status').default('pending'), // pending, processed, failed, spam, duplicate
  supportTicketId: integer('support_ticket_id').references(() => supportTickets.id),
  errorMessage: text('error_message'),
  processedAt: timestamp('processed_at'),
});

// New table select/insert types (webhooks and stage history)
export type EquipmentOption = typeof equipmentOptions.$inferSelect;
export type InsertEquipmentOption = typeof equipmentOptions.$inferInsert;
export type PipelineStageHistory = typeof pipelineStageHistory.$inferSelect;
export type InsertPipelineStageHistory = typeof pipelineStageHistory.$inferInsert;
export type PipelineStageSLA = typeof pipelineStageSLAs.$inferSelect;
export type InsertPipelineStageSLA = typeof pipelineStageSLAs.$inferInsert;
export type PipelineAnalytic = typeof pipelineAnalytics.$inferSelect;
export type InsertPipelineAnalytic = typeof pipelineAnalytics.$inferInsert;
export type PipelineStageReason = typeof pipelineStageReasons.$inferSelect;
export type InsertPipelineStageReason = typeof pipelineStageReasons.$inferInsert;
export type WebhookSubscription = typeof webhookSubscriptions.$inferSelect;
export type InsertWebhookSubscription = typeof webhookSubscriptions.$inferInsert;
export type WebhookEvent = typeof webhookEvents.$inferSelect;
export type InsertWebhookEvent = typeof webhookEvents.$inferInsert;
export type WebhookDelivery = typeof webhookDeliveries.$inferSelect;
export type InsertWebhookDelivery = typeof webhookDeliveries.$inferInsert;
export type MetaEvent = typeof metaEvents.$inferSelect;
export type InsertMetaEvent = typeof metaEvents.$inferInsert;
export type CustomerMetadata = typeof customerMetadata.$inferSelect;
export type InsertCustomerMetadata = typeof customerMetadata.$inferInsert;

// Scheduling system types
export type StaffProfile = typeof staffProfiles.$inferSelect;
export type InsertStaffProfile = typeof staffProfiles.$inferInsert;
export type StaffAvailability = typeof staffAvailability.$inferSelect;
export type InsertStaffAvailability = typeof staffAvailability.$inferInsert;
export type StaffLeave = typeof staffLeave.$inferSelect;
export type InsertStaffLeave = typeof staffLeave.$inferInsert;
export type JobSchedule = typeof jobSchedules.$inferSelect;
export type InsertJobSchedule = typeof jobSchedules.$inferInsert;
export type JobAssignment = typeof jobAssignments.$inferSelect;
export type InsertJobAssignment = typeof jobAssignments.$inferInsert;
export type SchedulingRule = typeof schedulingRules.$inferSelect;
export type InsertSchedulingRule = typeof schedulingRules.$inferInsert;
export type WeatherNote = typeof weatherNotes.$inferSelect;
export type InsertWeatherNote = typeof weatherNotes.$inferInsert;
export type ScheduleConflict = typeof scheduleConflicts.$inferSelect;
export type InsertScheduleConflict = typeof scheduleConflicts.$inferInsert;

// BOM types
export type BomItem = typeof bomItems.$inferSelect;
export type InsertBomItem = typeof bomItems.$inferInsert;
export type BomTemplate = typeof bomTemplates.$inferSelect;
export type InsertBomTemplate = typeof bomTemplates.$inferInsert;
export type BomSupplier = typeof bomSuppliers.$inferSelect;
export type InsertBomSupplier = typeof bomSuppliers.$inferInsert;
export type BomCustomItem = typeof bomCustomItems.$inferSelect;
export type InsertBomCustomItem = typeof bomCustomItems.$inferInsert;

// Support Ticket types
export type EquipmentProvider = typeof equipmentProviders.$inferSelect;
export type InsertEquipmentProvider = typeof equipmentProviders.$inferInsert;
export type SupportTicket = typeof supportTickets.$inferSelect;
export type InsertSupportTicket = typeof supportTickets.$inferInsert;
export type SupportTicketActivity = typeof supportTicketActivities.$inferSelect;
export type InsertSupportTicketActivity = typeof supportTicketActivities.$inferInsert;
export type SupportTicketImage = typeof supportTicketImages.$inferSelect;
export type InsertSupportTicketImage = typeof supportTicketImages.$inferInsert;
export type SupportTicketTag = typeof supportTicketTags.$inferSelect;
export type InsertSupportTicketTag = typeof supportTicketTags.$inferInsert;
export type SupportTicketTagAssignment = typeof supportTicketTagAssignments.$inferSelect;
export type InsertSupportTicketTagAssignment = typeof supportTicketTagAssignments.$inferInsert;
export type InboundEmailLog = typeof inboundEmailLogs.$inferSelect;
export type InsertInboundEmailLog = typeof inboundEmailLogs.$inferInsert;

// U2 tracking vocabulary only. U8 owns persistence/DDL for these facts.
export type TrackingAcquisitionSource = import('./tracking-contracts').AcquisitionSource;
export type TrackingCompletionChannel = import('./tracking-contracts').CompletionChannel;
export type TrackingMetaActionSource = import('./tracking-contracts').MetaActionSource;
export type TrackingOutcomeKey = import('./tracking-contracts').OutcomeKey;
export type TrackingWebsiteChoice = import('./tracking-contracts').WebsiteTrackingChoice;
export type TrackingProjectionDecision = import('./tracking-contracts').ProjectionPolicyDecision;

// ============================================================================
// OpenSolar API Response Types
// ============================================================================

/**
 * Generic paginated list response from OpenSolar API
 */
export interface OpenSolarApiList<T> {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: T[];
}

/**
 * OpenSolar Project (from API response)
 */
export interface OpenSolarApiProject {
  id: string | number;
  identifier?: string;
  name: string;
  address?: string;
  locality?: string;
  state?: string;
  zip?: string;
  country_iso2?: string;
  is_residential?: boolean;
  lat?: string;
  lon?: string;
  contacts?: string[] | any[];
  contacts_new?: any[];
  metadata?: any;
  created_at?: string;
  updated_at?: string;
  status?: string;
  url?: string;
}

/**
 * OpenSolar Design (from API response)
 */
export interface OpenSolarApiDesign {
  id: string | number;
  name?: string;
  project?: string | number;
  status?: string;
  created_at?: string;
  updated_at?: string;
  design_data?: any;
  url?: string;
}

/**
 * OpenSolar SDK Key (from API response)
 */
export interface OpenSolarApiSdkKey {
  id: string | number;
  key?: string;
  name?: string;
  org_id?: string;
  created_at?: string;
  is_active?: boolean;
  url?: string;
}

/**
 * OpenSolar Contact (from API response)
 */
export interface OpenSolarApiContact {
  id: string | number;
  first_name?: string;
  family_name?: string;
  email?: string;
  phone?: string;
  role?: string;
  url?: string;
  notes?: string;
}

/**
 * OpenSolar Bearer Token Generation Response
 */
export interface OpenSolarTokenResponse {
  access_token?: string;
  token_type?: string;
  expires_in?: number;
  refresh_token?: string;
  user?: {
    id: number;
    email: string;
    first_name?: string;
    last_name?: string;
  };
}

/**
 * OpenSolar Error Response
 */
export interface OpenSolarErrorResponse {
  error?: string;
  message?: string;
  details?: any;
  code?: number;
}

// ============================================================================
// Integration API tokens (scoped bearer tokens for third-party automation)
// ============================================================================

// Integration tokens - opaque bearer credentials for allow-listed automation routes.
// Only the sha256 of the secret segment is stored; the plaintext is shown once at creation.
export const integrationTokens = pgTable('integration_tokens', {
  id: serial('id').primaryKey(),
  tokenId: text('token_id').notNull().unique(), // public id segment of the token (dsm_<token_id>_<secret>)
  tokenHash: text('token_hash').notNull(), // sha256 hex of the secret segment
  name: text('name').notNull(),
  scopes: jsonb('scopes').$type<string[]>().notNull(), // e.g. ['survey:search', 'bom:read']
  createdByUserId: integer('created_by_user_id').references(() => users.id),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  expiresAt: timestamp('expires_at').notNull(),
  revokedAt: timestamp('revoked_at'),
  revokedByUserId: integer('revoked_by_user_id').references(() => users.id),
  rotatedFromTokenId: integer('rotated_from_token_id'), // integration_tokens.id of the predecessor
  overlapUntil: timestamp('overlap_until'), // set on the predecessor when rotated; null when not rotated
  lastUsedAt: timestamp('last_used_at'),
  readLimitPerMin: integer('read_limit_per_min').default(60).notNull(),
  writeLimitPerMin: integer('write_limit_per_min').default(30).notNull(),
  deleteLimitPerMin: integer('delete_limit_per_min').default(10).notNull(),
  notes: text('notes'),
});

// Integration audit log - one append-only row per token-authenticated request.
// Also the source of truth for per-token rate limiting (count rows in the trailing window).
export const integrationAuditLog = pgTable(
  'integration_audit_log',
  {
    id: bigserial('id', { mode: 'number' }).primaryKey(),
    tokenId: integer('token_id').references(() => integrationTokens.id),
    requestId: text('request_id').notNull(),
    method: text('method'),
    route: text('route'), // express route pattern (e.g. /api/bom/items/:id), never the raw URL
    resourceType: text('resource_type'),
    resourceId: integer('resource_id'),
    opClass: text('op_class'), // 'read' | 'write' | 'delete'
    statusCode: integer('status_code'),
    durationMs: integer('duration_ms'),
    sourceIp: text('source_ip'),
    changeSummary: jsonb('change_summary'),
    beforeImage: jsonb('before_image'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
  },
  (table) => [
    index('integration_audit_log_token_id_created_at_idx').on(table.tokenId, table.createdAt),
    index('integration_audit_log_request_id_idx').on(table.requestId),
  ]
);

export const insertIntegrationTokenSchema = createInsertSchema(integrationTokens);
export const insertIntegrationAuditLogSchema = createInsertSchema(integrationAuditLog);

export type IntegrationToken = typeof integrationTokens.$inferSelect;
export type InsertIntegrationToken = typeof integrationTokens.$inferInsert;
export type IntegrationAuditLogEntry = typeof integrationAuditLog.$inferSelect;
export type InsertIntegrationAuditLogEntry = typeof integrationAuditLog.$inferInsert;
